import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import RentIncreasePanel from "@/components/rentincrease/RentIncreasePanel";
import { DocRowActions } from "@/components/tenancy/DocRowActions";
import { MailboxScanPicker } from "@/components/messaging/MailboxScanPicker";
import api, { formatApiError, API, getToken } from "@/lib/api";
import { SurplusOfferBanner, SurplusOffsetDialog } from "@/components/tenancy/SurplusOffset";
import DunningCorrectionDialog from "@/components/tenancy/DunningCorrectionDialog";
import { SurplusRefundDialog } from "@/components/tenancy/SurplusRefund";
import { LedgerMobileList } from "@/components/tenancy/LedgerMobileList";
import PostingCategoryLabel from "@/components/tenancy/PostingCategoryLabel";
import CommunicationTab from "@/components/communication/CommunicationTab";
import { TENANCY_STATUS, TENANCY_TYPES, POSTING_CATEGORIES, POSTING_CATEGORY_GROUPS, DOCUMENT_CATEGORIES, formatEUR, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft, Plus, Calculator, Repeat, Trash, FileArrowUp, FileText, Download, X,
  PaperPlaneTilt, Clock, PencilSimple, House, Users, MapPin, Eye, Briefcase,
  CheckCircle, Warning, Info as InfoIcon, DotsThree, BellRinging, HandCoins, ArrowsClockwise,
  Check as CheckIcon,
  Receipt,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import MessagingDialog from "@/components/MessagingDialog";
import ChangeHistoryDialog from "@/components/ChangeHistoryDialog";
import DocumentPreviewDrawer from "@/components/DocumentPreviewDrawer";
import { DocMetaButton, docDate } from "@/components/documents/DocumentMeta";
import GenerateContractDialog from "@/components/GenerateContractDialog";
import TerminateDialog from "@/components/TerminateDialog";
import TerminationEditDialog from "@/components/TerminationEditDialog";
import DunningTriggerDialog from "@/components/DunningTriggerDialog";
import InstallmentPlanCard from "@/components/InstallmentPlanCard";
import DokumentenmappeTab from "@/components/DokumentenmappeTab";
import ProtocolsPanel from "@/components/ProtocolsPanel";
import AppointmentInviteDialog from "@/components/tenancy/AppointmentInviteDialog";
import CohabitantsPanel from "@/components/tenancy/CohabitantsPanel";
import OfficialDocumentsPanel from "@/components/tenancy/OfficialDocumentsPanel";
import KuendigungAssistant from "@/components/tenancy/KuendigungAssistant";
import { KeyFact, Info, Th, Td, StatCard, Tile } from "@/components/tenancy/TenancyBits";
import PostingDialog from "@/components/tenancy/PostingDialog";
import RuleDialog from "@/components/tenancy/RuleDialog";
import IndexRentSection from "@/components/tenancy/IndexRentSection";
import DepositAccountPanel from "@/components/tenancy/DepositAccountPanel";
import { EndOfTenancyActions } from "@/components/tenancy/EndOfTenancyActions";
import { PostingActionsMenu, PeriodBadge } from "@/components/tenancy/PostingActionsMenu";
import { TenancyDamagesTab } from "@/components/tenancy/TenancyDamagesTab";
import TenancyAmendments from "@/components/tenancy/TenancyAmendments";
import PreOwnershipReviewDialog from "@/components/PreOwnershipReviewDialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { TenancyDialog } from "@/pages/Tenancies";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// iter159n — Modul-Level Component: Value + kleiner "geändert"-Indikator
function AmendedValue({ amount, amendment, showArrow = true }) {
  if (!amendment) return <>{formatEUR(amount)}</>;
  const prev = amendment.previous_value;
  const nv = amendment.new_value;
  const isNew = prev === null || prev === undefined;
  const isUp = !isNew && Number(nv) > Number(prev);
  const isDown = !isNew && Number(nv) < Number(prev);
  const arrow = isNew ? "NEU" : (isUp ? "↑" : (isDown ? "↓" : "="));
  const colorCls = isNew
    ? "text-emerald-600 border-emerald-200 bg-emerald-50"
    : isUp
      ? "text-blue-600 border-blue-200 bg-blue-50"
      : isDown
        ? "text-rose-600 border-rose-200 bg-rose-50"
        : "text-slate-600 border-slate-200 bg-slate-50";
  const effFmt = amendment.effective_date ? formatDate(amendment.effective_date) : null;
  const prevFmt = isNew
    ? "—"
    : Number(prev).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
  const nvFmt = Number(nv || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
  const tip = effFmt
    ? `Vertragsänderung ab ${effFmt}: ${prevFmt} → ${nvFmt}${amendment.reason ? " · " + amendment.reason : ""}`
    : (amendment.reason || "Aktualisierter Wert");
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{formatEUR(amount)}</span>
      {showArrow && (
        <span
          className={`inline-flex items-center rounded border ${colorCls} px-1 py-0.5 text-[9px] font-medium leading-none cursor-help`}
          title={tip}
          data-testid="amended-value-indicator"
        >
          {arrow}
        </span>
      )}
    </span>
  );
}

export default function TenancyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  const canEditContractNumber = ["super_admin", "admin", "management"].includes(user?.role);
  const [t, setT] = useState(null);
  const [surplusDialogOpen, setSurplusDialogOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [dunningFixOpen, setDunningFixOpen] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [rules, setRules] = useState([]);
  const [docs, setDocs] = useState([]);
  const [openPosting, setOpenPosting] = useState(false);
  const [preOwnershipDialogOpen, setPreOwnershipDialogOpen] = useState(false);
  const [openRule, setOpenRule] = useState(false);
  const [openMsg, setOpenMsg] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  // iter145 — Amtl. Dok. „als Anlage in Editor" → beim Öffnen von MessagingDialog vorauswählen
  const [pendingMsgAttachments, setPendingMsgAttachments] = useState(null);
  // 2026-10 · Vorbelegter Betreff+Body (z. B. für Ratenplan-Erinnerung)
  const [pendingMsgSubject, setPendingMsgSubject] = useState("");
  const [pendingMsgExtra, setPendingMsgExtra] = useState({});
  const [pendingMsgBody, setPendingMsgBody] = useState("");
  const [openGenerateContract, setOpenGenerateContract] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [openTerminate, setOpenTerminate] = useState(false);
  const [openTerminateEdit, setOpenTerminateEdit] = useState(false);
  const [openDunningTrigger, setOpenDunningTrigger] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openKuendigungAssistant, setOpenKuendigungAssistant] = useState(false);
  // 2026-02 — Edit-Dialog inline auf der Detail-Seite (kein Umweg über Liste)
  const [openEdit, setOpenEdit] = useState(false);
  const [editProperties, setEditProperties] = useState([]);
  const [editContacts, setEditContacts] = useState([]);
  const openEditInline = async () => {
    try {
      const [pp, cc] = await Promise.all([
        api.get("/properties"),
        api.get("/contacts?role=tenant&limit=500"),
      ]);
      setEditProperties(pp.data || []);
      setEditContacts(cc.data || []);
      setOpenEdit(true);
    } catch (e) {
      toast.error("Konnte Bearbeitungsdaten nicht laden");
    }
  };
  const reloadEditContacts = async () => {
    try {
      const cc = await api.get("/contacts?role=tenant&limit=500");
      setEditContacts(cc.data || []);
    } catch {}
  };
  const [previewDoc, setPreviewDoc] = useState(null);
  const [score, setScore] = useState(null);
  // 2026-08 — Ratenzahlungspläne des MV (mit Health-Status)
  const [paymentPlans, setPaymentPlans] = useState([]);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingContractNum, setEditingContractNum] = useState(false);
  const [newContractNum, setNewContractNum] = useState("");
  const [contractNumCheck, setContractNumCheck] = useState({ state: "idle" });
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "ledger");
  const [officialPreselect, setOfficialPreselect] = useState(null);
  // iter164c — Zeitraum-Filter fürs Mietkonto (wirkt auch auf PDF-Export).
  // Presets: 'all' | 'current_year' | 'last_6m' | 'custom'
  const [ledgerPeriod, setLedgerPeriod] = useState({ preset: "all", from: "", to: "" });
  const ledgerRange = useMemo(() => {
    const today = new Date();
    if (ledgerPeriod.preset === "current_year") {
      const y = today.getFullYear();
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    }
    if (ledgerPeriod.preset === "last_6m") {
      const to = today.toISOString().slice(0, 10);
      const d = new Date(today); d.setMonth(d.getMonth() - 6);
      return { from: d.toISOString().slice(0, 10), to };
    }
    if (ledgerPeriod.preset === "custom" && (ledgerPeriod.from || ledgerPeriod.to)) {
      return { from: ledgerPeriod.from || null, to: ledgerPeriod.to || null };
    }
    return { from: null, to: null };
  }, [ledgerPeriod]);
  // 2026-02 — Deep-Link ?tab=kaution|... aus Dashboard-Widget
  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    if (t === "kaution" || t === "deposit") setActiveTab("deposit");
    else if (t) setActiveTab(t);
  }, []);
  const fileInputRef = useRef(null);

  // iter159n — Effektive Werte aus aktiven rent_rules (heute) berechnen.
  // Ermöglicht der Übersicht oben, immer die aktuell gültigen Beträge
  // anzuzeigen — auch nach einer Vertragsänderung.
  const effectiveRates = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const map = {};
    for (const r of rules || []) {
      const start = r.start_date || "0000-01-01";
      const end = r.end_date;
      if (start <= today && (!end || end >= today)) {
        // Wenn mehrere aktive Regeln pro Kategorie: die mit dem spätesten Start gewinnt.
        const existing = map[r.category];
        if (!existing || (existing.start_date || "") < start) {
          map[r.category] = r;
        }
      }
    }
    return map;
  }, [rules]);

  // Helper: aktueller Wert für eine Kategorie (fallback auf tenancy-Feld)
  const eff = (category, fallback) => {
    const r = effectiveRates[category];
    return r ? Number(r.amount || 0) : Number(fallback || 0);
  };
  const effAmend = (category) => effectiveRates[category]?.amendment_info || null;

  // Echtzeit-Eindeutigkeitsprüfung Vertragsnr.
  useEffect(() => {
    if (!editingContractNum) return;
    const val = (newContractNum || "").trim();
    if (!val || val === t?.contract_number) {
      setContractNumCheck({ state: "idle" });
      return;
    }
    setContractNumCheck({ state: "checking" });
    const t2 = setTimeout(async () => {
      try {
        const { data } = await api.get("/tenancies/contract-number/check", {
          params: { value: val, exclude_id: id },
        });
        setContractNumCheck({
          state: data.available ? "available" : "taken",
          message: data.available ? "Verfügbar" : "Wird bereits verwendet",
        });
      } catch {
        setContractNumCheck({ state: "idle" });
      }
    }, 350);
    return () => clearTimeout(t2);
  }, [newContractNum, editingContractNum, id, t?.contract_number]);

  const saveContractNum = async () => {
    const val = (newContractNum || "").trim();
    if (!val) return;
    try {
      await api.patch(`/tenancies/${id}/contract-number`, { contract_number: val });
      toast.success(`Vertragsnummer auf "${val}" aktualisiert`);
      setEditingContractNum(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const deleteTenancy = async () => {
    try {
      await api.delete(`/tenancies/${id}`);
      toast.success("Mietverhältnis archiviert");
      setOpenDelete(false);
      navigate("/mietverhaeltnisse");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [includePreOwnership, setIncludePreOwnership] = useState(false);
  // 2026-02 — Verwaltungs- & Eigentumsbeginn (aus management-context)
  const [mgmtStart, setMgmtStart] = useState(null);         // effektiver Cutoff
  const [ownershipStart, setOwnershipStart] = useState(null);
  const [mgmtStartRaw, setMgmtStartRaw] = useState(null);   // property.management_start / Verwaltervertrag
  useEffect(() => {
    if (!t?.id) return;
    (async () => {
      try {
        const { data } = await api.get(`/tenancies/${t.id}/management-context`);
        setMgmtStart(data?.effective_soll_cutoff || data?.management_start_date || null);
        setOwnershipStart(data?.ownership_start_date || null);
        setMgmtStartRaw(data?.management_start_date || null);
      } catch (e) { /* still */ }
    })();
  }, [t?.id]);
  // 2026-02 — Mehrfachauswahl im Mietkonto
  const [selectedPostings, setSelectedPostings] = useState(new Set());
  const toggleSelectPosting = (pid) => {
    setSelectedPostings((prev) => {
      const n = new Set(prev);
      if (n.has(pid)) n.delete(pid); else n.add(pid);
      return n;
    });
  };
  const clearSelection = () => setSelectedPostings(new Set());
  const selectAllVisible = () => {
    const ids = (ledger?.postings || []).map((p) => p.id);
    setSelectedPostings(new Set(ids));
  };

  const bulkDeleteSelected = async () => {
    const ids = Array.from(selectedPostings);
    if (!ids.length) return;
    const ok = await confirm({
      title: `${ids.length} Buchung(en) löschen?`,
      description: "Alle ausgewählten Buchungen werden endgültig entfernt. Buchungen aus Split-Zahlungen werden inklusive Split-Gruppe (Bank-Zahlung) mitentfernt und die Bank-Buchung wird wieder frei verfügbar.",
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/tenancies/${id}/postings/bulk/delete`, {
        posting_ids: ids,
      });
      toast.success(`${data.deleted_count} Buchung(en) gelöscht${data.bank_txs_unlinked ? ` · ${data.bank_txs_unlinked} Bank-Buchung(en) wieder frei` : ""}`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk-Löschen fehlgeschlagen");
    }
  };

  const bulkUnsplitSelected = async () => {
    const ids = Array.from(selectedPostings);
    const posts = (ledger?.postings || []).filter((p) => ids.includes(p.id) && p.split_info?.split_count > 1);
    if (!posts.length) {
      toast.info("Keine der gewählten Buchungen ist gesplittet.");
      return;
    }
    const groups = new Set(posts.map((p) => p.bank_transaction_id || p.manual_split_group_id));
    const ok = await confirm({
      title: `${groups.size} Split-Gruppe(n) zusammenführen?`,
      description: "Alle Anteile jeder gewählten Split-Gruppe werden zu je einer Buchung mit dem Gesamtbetrag zusammengeführt.",
      confirmLabel: "Zusammenführen",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/tenancies/${id}/postings/bulk-unsplit`, { posting_ids: posts.map((p) => p.id) });
      toast.success(`${data.groups_merged} Split-Gruppe(n) zusammengeführt · ${data.postings_removed} Anteile → ${data.groups_merged} Buchung(en)`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zusammenführen fehlgeschlagen");
    }
  };

  const bulkUnlinkSelected = async () => {
    // Sammle Bank-TX-IDs aus den ausgewählten Postings
    const ids = Array.from(selectedPostings);
    const posts = (ledger?.postings || []).filter((p) => ids.includes(p.id) && p.bank_transaction_id);
    const txIds = Array.from(new Set(posts.map((p) => p.bank_transaction_id)));
    if (!txIds.length) {
      toast.info("Keine der gewählten Buchungen ist mit einer Bank-Zahlung verknüpft.");
      return;
    }
    const ok = await confirm({
      title: `${txIds.length} Bank-Verknüpfung(en) aufheben?`,
      description: 'Die Bank-Buchungen werden wieder als „unzugeordnet" markiert. Alle zugehörigen Ist-Postings (inkl. Split-Gruppen) im Mietkonto werden entfernt.',
      confirmLabel: "Aufheben",
      danger: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/bank/transactions/bulk/unlink`, { ids: txIds });
      toast.success(`${data.unlinked} Bank-Buchung(en) freigegeben · ${data.postings_removed} Ist-Buchung(en) entfernt${data.split_groups_removed ? ` · ${data.split_groups_removed} Split-Gruppe(n)` : ""}`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aufheben fehlgeschlagen");
    }
  };

  const load = async () => {
    // iter160i — zuerst Kautions-Status refreshen, dann Tenancy laden
    try { await api.get(`/deposit-accounts/${id}`); } catch { /* silent */ }
    const [{ data: tt }, { data: pp }, { data: rr }, { data: dd }] = await Promise.all([
      api.get(`/tenancies/${id}`),
      api.get(`/tenancies/${id}/postings`, { params: { include_pre_ownership: includePreOwnership } }),
      api.get(`/tenancies/${id}/rules`),
      api.get(`/documents`, { params: { tenancy_id: id } }),
    ]);
    setT(tt);
    setLedger(pp);
    setRules(rr);
    setDocs(dd);
    try {
      const { data: sc } = await api.get(`/tenant-score/${id}`);
      setScore(sc);
    } catch { /* ignore */ }
    try {
      const { data: pl } = await api.get(`/dunning/payment-plans?tenancy_id=${id}`);
      setPaymentPlans(pl || []);
    } catch { /* ignore */ }
    // iter167 — Trigger für externe Sub-Widgets (ArrearsBreakdownCard etc.)
    setReloadTick((n) => n + 1);
  };
  useEffect(() => { load(); }, [id]);
  // 2026-10 · Event-Bus: Ratenplan-Erinnerung → MessagingDialog vorbelegen
  useEffect(() => {
    const handler = (ev) => {
      const d = ev.detail || {};
      if (d.tenancy_id && d.tenancy_id !== id) return;
      setPendingMsgSubject(d.subject || "");
      setPendingMsgBody(d.body || "");
      setPendingMsgAttachments(null);
      setOpenMsg(true);
    };
    window.addEventListener("domivus:compose-message", handler);
    return () => window.removeEventListener("domivus:compose-message", handler);
  }, [id]);
  // iter135 — Ledger neu laden, wenn Vor-Eigentümer-Toggle umgeschaltet wird.
  // iter164c — Auch bei Zeitraum-Filter neu laden.
  useEffect(() => {
    if (t) {
      const params = { include_pre_ownership: includePreOwnership };
      if (ledgerRange.from) params.from_date = ledgerRange.from;
      if (ledgerRange.to) params.to_date = ledgerRange.to;
      api.get(`/tenancies/${id}/postings`, { params })
        .then(({ data }) => setLedger(data)).catch(() => { /* egal */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includePreOwnership, ledgerRange.from, ledgerRange.to]);

  if (!t) return <div className="text-sm text-slate-500">Lade...</div>;
  const status = TENANCY_STATUS[t.status] || { label: t.status, className: "bg-slate-100" };

  const generateSoll = async () => {
    try {
      await api.post(`/tenancies/${id}/generate-soll?months=1`);
      toast.success("Sollstellung erzeugt");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyRule = async (ruleId) => {
    try {
      const { data } = await api.post(`/tenancies/${id}/rules/${ruleId}/apply`);
      toast.success(`${data.created} Sollstellung(en) erzeugt`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const regenerateSoll = async () => {
    const ok = await confirm({
      title: "Soll-Buchungen ab heute neu berechnen?",
      description: "Alle zukünftigen (ab heute) Soll-Buchungen werden gelöscht und aus den aktuellen Sollstellungs-Regeln mit korrekter Werktags-Fälligkeit neu erzeugt. Bereits verbuchte Ist-Zahlungen bleiben unverändert.",
      confirmLabel: "Neu berechnen",
      danger: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/tenancies/${id}/regenerate-soll`);
      toast.success(`${data.deleted} alte Sollstellung(en) gelöscht · ${data.created} neu erzeugt`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // 2026-08 — Für Verträge, die mitten im Monat begonnen haben und deshalb
  // keine anteilige Anfangs-Buchung erhalten haben, bietet dieser Aufruf
  // eine vollständige Neuberechnung ab Vertragsbeginn.
  // iter164d — Wenn manuelle oder BKA-Buchungen existieren, warnen und
  // dem User erlauben zu wählen, ob diese erhalten bleiben.
  const regenerateSollFull = async () => {
    let preserveMsg = "";
    try {
      const { data: p } = await api.get(`/tenancies/${id}/reset-account/preview`);
      const parts = [];
      if (p.manual_entries > 0) parts.push(`${p.manual_entries} manuelle Buchung(en)`);
      if (p.bka_postings > 0) parts.push(`${p.bka_postings} BKA-Nachzahlung(en)`);
      if (parts.length) preserveMsg = `\n\n⚠ Betroffen: ${parts.join(" · ")}. Diese werden AUTOMATISCH erhalten (BKA & manuell). Ist-Zahlungen bleiben ebenfalls.`;
    } catch { /* silent */ }
    const ok = await confirm({
      title: "ALLE Sollstellungen ab Vertragsbeginn neu berechnen?",
      description: "Alle Soll-Buchungen ab Vertragsbeginn werden gelöscht und aus den aktuellen Regeln neu erzeugt — inkl. anteiliger Anfangs-Buchung, wenn der Vertrag mitten im Monat startet. Ist-Zahlungen bleiben unangetastet." + preserveMsg,
      confirmLabel: "Alles neu berechnen",
      danger: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/tenancies/${id}/regenerate-soll?include_history=true`);
      toast.success(`${data.deleted} alte Sollstellung(en) gelöscht · ${data.created} neu erzeugt (ab ${data.from_date})`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Sollstellungen auch vor dem Verwalterbeginn (z. B. übernommene Rückstände, Altverträge)
  const regenerateSollPreMgmt = async () => {
    const ok = await confirm({
      title: "Sollstellungen auch vor dem Verwalterbeginn erzeugen?",
      description: "Alle Soll-Buchungen ab Vertragsbeginn werden aus den Sollstellungs-Regeln neu erzeugt – ausdrücklich auch für Zeiträume vor Ihrem Verwalterbeginn (z. B. übernommene Rückstände oder bereits beendete Altverträge). Ist-Zahlungen, BKA- und manuelle Buchungen bleiben erhalten. Diese Sollstellungen bleiben bei späteren Neuberechnungen bestehen.",
      confirmLabel: "Erzeugen",
      danger: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/tenancies/${id}/regenerate-soll?include_history=true&include_pre_management=true`);
      toast.success(`${data.deleted} alte Sollstellung(en) gelöscht · ${data.created} neu erzeugt (ab ${data.from_date})`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revertAllPreOwnership = () => setPreOwnershipDialogOpen(true);

  const resetAccount = async () => {
    // iter164d — Preview: manuelle + BKA anzeigen und User entscheiden lassen
    let preview = { manual_entries: 0, bka_postings: 0, bank_linked: 0 };
    try {
      const { data } = await api.get(`/tenancies/${id}/reset-account/preview`);
      preview = data;
    } catch { /* silent */ }
    const details = [];
    if (preview.manual_entries > 0) details.push(`${preview.manual_entries} manuelle Buchung(en)`);
    if (preview.bka_postings > 0) details.push(`${preview.bka_postings} BKA-Nachzahlung(en)`);
    if (preview.bank_linked > 0) details.push(`${preview.bank_linked} Bank-Zuordnung(en)`);
    const description = 'Diese Aktion löscht Soll- und Ist-Buchungen. Bank-Zuordnungen werden auf "nicht zugeordnet" zurückgesetzt.\n\nEmpfehlung (Default): BKA-Nachzahlungen werden ERHALTEN, damit sie nicht erneut manuell nachgetragen werden müssen.\n\n' + (details.length ? `Betroffen: ${details.join(" · ")}.` : "");
    const ok = await confirm({
      title: "Mietkonto zurücksetzen?",
      description,
      confirmLabel: "Zurücksetzen (BKA behalten)",
      danger: true,
    });
    if (!ok) return;
    // Zusätzlicher Prompt für manuelle Buchungen
    let preserveManual = false;
    if (preview.manual_entries > 0) {
      preserveManual = window.confirm(`${preview.manual_entries} manuelle Buchung(en) gefunden.\n\nOK = Manuelle Buchungen ERHALTEN\nAbbrechen = Manuelle Buchungen LÖSCHEN`);
    }
    try {
      const { data } = await api.post(`/tenancies/${id}/reset-account`, null, {
        params: { preserve_bka: true, preserve_manual: preserveManual },
      });
      toast.success(`Mietkonto zurückgesetzt: ${data.deleted_soll} Soll · ${data.deleted_ist} Ist gelöscht · ${data.reset_bank_tx} Bank-Zuordnung(en) gelöst`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };


  const deleteRule = async (ruleId) => {
    const ok = await confirm({
      title: "Regel löschen?",
      description: "Die Sollstellungs-Regel wird gelöscht. Vorhandene Buchungen bleiben erhalten.",
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    await api.delete(`/tenancies/${id}/rules/${ruleId}`);
    load();
  };

  const deletePosting = async (postingId) => {
    const ok = await confirm({
      title: "Buchung löschen?",
      description: "Die Buchung wird endgültig entfernt.",
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/tenancies/${id}/postings/${postingId}`);
      toast.success("Buchung gelöscht");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      // 409 → Split-Gruppe: Rückfrage anzeigen
      if (e?.response?.status === 409 && d?.code === "split_group_confirm") {
        const ok2 = await confirm({
          title: "Aufgeteilte Bank-Zahlung",
          description: `${d.message}\n\nEs werden ${d.split_count} Buchungen entfernt (Bank-Zahlung ${d.bank_amount?.toLocaleString?.("de-DE", { minimumFractionDigits: 2 }) || ""} € vom ${d.bank_booking_date || ""}).`,
          confirmLabel: "Alle entfernen",
          danger: true,
        });
        if (!ok2) return;
        try {
          const { data } = await api.delete(`/tenancies/${id}/postings/${postingId}`, {
            params: { cascade_split: true },
          });
          toast.success(`Split-Gruppe entfernt · ${data.deleted_count} Buchung(en) gelöscht · Bank-Buchung wieder frei`);
          load();
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || "Löschen fehlgeschlagen");
        }
        return;
      }
      toast.error(typeof d === "string" ? d : "Löschen fehlgeschlagen");
    }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name);
    fd.append("category", "Mietvertrag");
    fd.append("doc_type", "general");
    fd.append("direction", "in");
    fd.append("linked_tenancy_id", id);
    if (t.property_id) fd.append("linked_property_id", t.property_id);
    if (t.tenant_contact_ids?.[0]) fd.append("linked_contact_id", t.tenant_contact_ids[0]);
    try {
      await fetch(`${API}/documents/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      }).then((r) => { if (!r.ok) throw new Error("Upload fehlgeschlagen"); });
      toast.success("Dokument hochgeladen");
      e.target.value = "";
      load();
    } catch (err) { toast.error(err.message); }
  };

  const downloadDoc = async (doc) => {
    const r = await fetch(`${API}/documents/${doc.id}/download`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.original_filename || doc.title;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteDoc = async (doc) => {
    const ok = await confirm({
      title: "Dokument löschen?",
      description: `„${doc.title || doc.original_filename || "Unbenannt"}" wird endgültig aus dieser Mappe entfernt (wird archiviert und ist nicht mehr sichtbar).`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Dokument gelöscht");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleNotifyOwner = async (doc) => {
    try {
      const next = !doc.notify_owner;
      await api.patch(`/documents/${doc.id}`, { notify_owner: next });
      toast.success(next ? "Zur Kenntnis für Eigentümer markiert" : "Kenntnisnahme-Marker entfernt");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeDocCategory = async (doc, newCategory) => {
    if (!newCategory || newCategory === doc.category) return;
    try {
      await api.patch(`/documents/${doc.id}`, { category: newCategory });
      toast.success(`Kategorie: ${newCategory}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="tenancy-detail">
      <button
        type="button"
        onClick={() => {
          if (window.history.length > 1) {
            navigate(-1);
          } else {
            navigate("/mietverhaeltnisse");
          }
        }}
        className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
        data-testid="tenancy-back-btn"
      >
        <ArrowLeft size={14} /> Zurück
      </button>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="tenancy-hero">
        {/* HERO: Vertragsnummer + Status + Aktionen + Score */}
        <div className="px-6 sm:px-8 pt-6 pb-5 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold font-mono flex items-center gap-1.5" data-testid="tenancy-contract-number">
                {editingContractNum ? (
                  <div className="flex items-center gap-1.5">
                    <Input
                      value={newContractNum}
                      onChange={(e) => setNewContractNum(e.target.value)}
                      className="h-6 w-44 text-xs font-mono"
                      data-testid="contract-number-input"
                      autoFocus
                    />
                    {contractNumCheck.state === "available" && (
                      <span className="text-[10px] text-emerald-700 font-medium">✓ {contractNumCheck.message}</span>
                    )}
                    {contractNumCheck.state === "taken" && (
                      <span className="text-[10px] text-rose-700 font-medium">⚠ {contractNumCheck.message}</span>
                    )}
                    {contractNumCheck.state === "checking" && (
                      <span className="text-[10px] text-slate-500">Prüfe…</span>
                    )}
                    <Button
                      size="sm" className="h-6 px-2 text-[10px] bg-slate-900"
                      disabled={contractNumCheck.state === "taken" || contractNumCheck.state === "checking" || !newContractNum.trim() || newContractNum.trim() === t.contract_number}
                      onClick={saveContractNum}
                      data-testid="contract-number-save"
                    >
                      Speichern
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => setEditingContractNum(false)}>
                      Abbrechen
                    </Button>
                  </div>
                ) : (
                  <>
                    <span>{t.contract_number}</span>
                    {canEditContractNumber && (
                      <button
                        type="button"
                        onClick={() => { setNewContractNum(t.contract_number || ""); setEditingContractNum(true); }}
                        className="text-slate-400 hover:text-slate-900 transition"
                        title="Vertragsnummer bearbeiten"
                        data-testid="contract-number-edit"
                      >
                        <PencilSimple size={11} />
                      </button>
                    )}
                  </>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 mt-1">
                {t.tenant_names && t.tenant_names.length > 0 ? t.tenant_names.join(" & ") : "Mietverhältnis"}
              </h1>
              <div className="mt-1.5 text-sm text-slate-600 flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${status.className}`}>{status.label}</span>
                <span className="text-slate-400">·</span>
                <span>{TENANCY_TYPES[t.tenancy_type] || t.tenancy_type}</span>
                {t.rent_type === "index" && (
                  <>
                    <span className="text-slate-400">·</span>
                    <span className="text-indigo-700 font-medium">Indexmiete</span>
                  </>
                )}
              </div>
            </div>

            {/* Rechts: Aktionen */}
            <div className="flex items-center gap-2 flex-wrap w-full lg:w-auto lg:shrink-0">
              <Button
                size="sm"
                variant="outline"
                className="h-9 gap-1.5"
                onClick={openEditInline}
                data-testid="tenancy-edit-btn"
              >
                <PencilSimple size={14} /> Bearbeiten
              </Button>
              {(() => {
                const isEnded = t?.termination_status === "cancelled" || t?.termination_status === "ended";
                return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm" variant="outline" className="h-9 gap-1.5"
                      data-testid="tenancy-more-actions"
                    >
                      <DotsThree size={16} /> Mehr
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem className="gap-2" onClick={() => setInviteOpen(true)} data-testid="tenancy-appointment-invite-btn">
                      Termin-Einladung Übergabe / Rücknahme
                    </DropdownMenuItem>
                    {!isEnded && (
                    <DropdownMenuItem
                      className={`gap-2 ${t?.dunning_paused ? "text-emerald-700 focus:text-emerald-800 focus:bg-emerald-50" : ""}`}
                      onClick={async () => {
                        try {
                          if (t?.dunning_paused) {
                            await api.post(`/tenancies/${t.id}/dunning-pause`, { paused: false });
                            toast.success("Mahnstopp aufgehoben");
                          } else {
                            const reason = window.prompt("Grund für den Mahnstopp?", "");
                            if (reason === null) return;
                            await api.post(`/tenancies/${t.id}/dunning-pause`, { paused: true, reason });
                            toast.success("Mahnstopp aktiviert");
                          }
                          window.location.reload();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
                      }}
                      data-testid="tenancy-dunning-pause-btn"
                    >
                      {t?.dunning_paused ? "✓ Mahnstopp aufheben" : "Mahnstopp aktivieren"}
                    </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={async () => {
                        try {
                          const { data: ctx } = await api.get(`/tenancies/${t.id}/management-context`);
                          if (!ctx.can_balance_pre_management) {
                            toast.info("Kein Vor-Verwalter-Zeitraum vorhanden — MV-Beginn liegt nach Verwalterbeginn oder es ist kein Verwaltervertrag hinterlegt.");
                            return;
                          }
                          // iter155 — Vorher Preview holen, ob es Zinsen/Gebühren gibt
                          const untilDate = new Date(ctx.management_start_date);
                          untilDate.setDate(untilDate.getDate() - 1);
                          const untilIso = untilDate.toISOString().slice(0, 10);
                          let stale = { count: 0, sum: 0 };
                          try {
                            const { data } = await api.get(`/tenancies/${t.id}/stale-dunning-charges`, {
                              params: { until_date: untilIso },
                            });
                            stale = data;
                          } catch { /* ignore preview errors */ }
                          const staleHint = stale.count > 0
                            ? `\n\n⚠ ${stale.count} Verzugszins-/Mahngebühr-Buchung${stale.count === 1 ? "" : "en"} im Wert von ${stale.sum.toFixed(2)} € existieren aus dem Zeitraum davor. Sollen diese mit entfernt werden? Sie basieren auf Rückständen, die jetzt ausgeglichen werden.`
                            : "";
                          const purge = stale.count > 0
                            ? await window._domivusConfirm(`Alle offenen Salden bis ${new Date(ctx.management_start_date).toLocaleDateString("de-DE")} als ausgeglichen markieren?${staleHint}`)
                            : await window._domivusConfirm(`Alle offenen Salden bis ${new Date(ctx.management_start_date).toLocaleDateString("de-DE")} als ausgeglichen markieren? Verwalterbeginn: ${ctx.management_start_date}`);
                          if (stale.count === 0 && !purge) return;
                          const { data: res } = await api.post(`/tenancies/${t.id}/balance-until`, {
                            purge_stale_charges: stale.count > 0 && purge,
                          });
                          const msgs = [];
                          if (res.created_count > 0)
                            msgs.push(`${res.created_count} Auto-Ausgleichsbuchung${res.created_count === 1 ? "" : "en"} (${res.amount_total.toFixed(2)} €)`);
                          if (res.purge_result?.deleted_count)
                            msgs.push(`${res.purge_result.deleted_count} Zinsen/Gebühren-Buchung${res.purge_result.deleted_count === 1 ? "" : "en"} entfernt (${res.purge_result.amount_total.toFixed(2)} €)`);
                          if (msgs.length) toast.success(msgs.join(" · "));
                          else toast.info("Keine offenen Sollstellungen vor Verwalterbeginn");
                          window.location.reload();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
                      }}
                      data-testid="tenancy-balance-until-btn"
                    >
                      Vor-Verwalter-Salden ausgleichen
                    </DropdownMenuItem>
                    {/* iter155 — Reparieren: stale Verzugszinsen/Mahngebühren nachträglich entfernen */}
                    <DropdownMenuItem
                      className="gap-2"
                      data-testid="tenancy-purge-stale-charges-btn"
                      onClick={async () => {
                        try {
                          const untilIso = new Date().toISOString().slice(0, 10);
                          const { data: preview } = await api.get(`/tenancies/${t.id}/stale-dunning-charges`, {
                            params: { until_date: untilIso },
                          });
                          if (!preview.count) {
                            toast.info("Keine Verzugszinsen oder Mahngebühren vorhanden — nichts zu bereinigen.");
                            return;
                          }
                          // Nur die als „stale" identifizieren, deren Rückstand <= 0 ist
                          if (!await window._domivusConfirm(
                            `${preview.count} Verzugszins-/Mahngebühr-Buchung${preview.count === 1 ? "" : "en"} im Wert von ${preview.sum.toFixed(2)} € gefunden.\n\n` +
                            `Diese werden entfernt, wenn der zugrunde liegende Rückstand am jeweiligen Buchungsdatum bereits ausgeglichen war (z. B. durch nachträgliche Vor-Verwalter-Zahlungen).\n\n` +
                            `Fortfahren?`,
                          )) return;
                          const { data } = await api.post(`/tenancies/${t.id}/purge-stale-dunning-charges`, {
                            until_date: untilIso,
                          });
                          if (data.deleted_count)
                            toast.success(`${data.deleted_count} Buchung${data.deleted_count === 1 ? "" : "en"} entfernt (${data.amount_total.toFixed(2)} €). Mahn-Status zurückgesetzt.`);
                          else
                            toast.info("Keine der Zinsen/Gebühren war überflüssig — Mietkonto bleibt unverändert.");
                          window.location.reload();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
                      }}
                    >
                      Stale Verzugszinsen/Mahngebühren bereinigen
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={async () => {
                        if (!await window._domivusConfirm("Wirklich ALLE automatisch generierten Vor-Verwalter-Ausgleiche entfernen? Dies solltest du nur tun, wenn du den vollständigen Kontoauszug bereits importiert hast.")) return;
                        try {
                          const { data } = await api.delete(`/tenancies/${t.id}/auto-settlements`);
                          if (data.deleted_count > 0)
                            toast.success(`${data.deleted_count} Auto-Ausgleichsbuchung${data.deleted_count === 1 ? "" : "en"} entfernt.`);
                          else
                            toast.info("Keine Auto-Ausgleiche vorhanden");
                          window.location.reload();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
                      }}
                      data-testid="tenancy-reset-auto-settlements-btn"
                    >
                      Alle Auto-Ausgleiche entfernen
                    </DropdownMenuItem>
                    {!isEnded && (
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={() => setOpenDunningTrigger(true)}
                      data-testid="tenancy-dunning-trigger-btn"
                    >
                      <Receipt size={14} /> Zahlungsaufforderung erstellen …
                    </DropdownMenuItem>
                    )}
                    {!isEnded && (
                    <DropdownMenuItem
                      className="text-red-700 focus:text-red-800 focus:bg-red-50 gap-2"
                      onClick={() => setOpenTerminate(true)}
                      data-testid="tenancy-terminate-btn"
                    >
                      <X size={14} /> Kündigen
                    </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                );
              })()}
              <Button
                size="sm"
                className="h-9 bg-slate-900 hover:bg-slate-800 gap-1.5"
                onClick={() => setOpenMsg(true)}
                data-testid="open-messaging-btn"
              >
                <PaperPlaneTilt size={14} /> Schreiben
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-9 w-9 p-0"
                onClick={() => setOpenHistory(true)}
                title="Änderungsverlauf"
                data-testid="open-history-btn"
              >
                <Clock size={14} />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-9 w-9 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                onClick={() => setOpenDelete(true)}
                title="Löschen / Archivieren"
                data-testid="tenancy-delete-btn"
              >
                <Trash size={14} />
              </Button>
            </div>
          </div>
        </div>

        {/* INHALT: Mieter | Objekt/Einheit | Saldo */}
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
          {/* Mieter */}
          <div className="px-6 sm:px-8 py-5">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">
              <Users size={11} /> Mieter
            </div>
            {t.tenants && t.tenants.length > 0 ? (
              <ul className="space-y-2" data-testid="tenancy-hero-tenants">
                {t.tenants.map((c) => (
                  <li key={c.id} className="text-sm">
                    {c.id ? (
                      <Link
                        to={`/kontakte/${c.id}`}
                        className="font-medium text-slate-900 hover:text-teal-700 hover:underline underline-offset-2"
                        data-testid={`tenancy-hero-tenant-link-${c.id}`}
                        title="Kontakt-Übersicht öffnen"
                      >
                        {c.label}
                      </Link>
                    ) : (
                      <div className="font-medium text-slate-900">{c.label}</div>
                    )}
                    {(c.email || c.phone || c.mobile) && (
                      <div className="text-[11px] text-slate-500 truncate">
                        {c.email || c.mobile || c.phone}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-slate-400 italic">Keine Mieter zugeordnet</div>
            )}
            {t.persons_count && (
              <div className="mt-3 text-[11px] text-slate-500">
                {t.persons_count} Person{t.persons_count === 1 ? "" : "en"} im Haushalt
              </div>
            )}
          </div>

          {/* Objekt + Einheit(en) */}
          <div className="px-6 sm:px-8 py-5">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">
              <House size={11} /> Objekt &amp; Einheit
            </div>
            <div className="text-sm" data-testid="tenancy-hero-property">
              {t.property_id ? (
                <Link
                  to={`/objekte/${t.property_id}`}
                  className="font-medium text-slate-900 hover:text-teal-700 hover:underline"
                  data-testid="tenancy-hero-property-link"
                >
                  {t.property_name || "—"}
                </Link>
              ) : (
                <div className="font-medium text-slate-900">{t.property_name || "—"}</div>
              )}
              {t.property_address && (
                <div className="text-[11px] text-slate-500 flex items-start gap-1 mt-0.5">
                  <MapPin size={10} className="mt-0.5 shrink-0" />
                  <span>
                    {t.property_address.street} {t.property_address.house_number}, {t.property_address.zip} {t.property_address.city}
                  </span>
                </div>
              )}
              <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="tenancy-hero-units">
                {t.property_id && t.unit_id ? (
                  <Link
                    to={`/objekte/${t.property_id}/einheiten/${t.unit_id}`}
                    className="font-mono text-[12px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium hover:bg-teal-50 hover:text-teal-800 transition-colors"
                    data-testid="tenancy-hero-unit-link"
                  >
                    {t.unit_label || "—"}
                    {t.unit_location ? ` · ${t.unit_location}` : ""}
                  </Link>
                ) : (
                  <span className="font-mono text-[12px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium">
                    {t.unit_label || "—"}
                    {t.unit_location ? ` · ${t.unit_location}` : ""}
                  </span>
                )}
                {(t.additional_unit_labels || []).map((lab, i) => (
                  <span
                    key={i}
                    className={`font-mono text-[11px] px-2 py-0.5 rounded-md font-medium ${
                      t.unit_grouping === "unified"
                        ? "bg-amber-50 text-amber-800 border border-amber-200"
                        : "bg-indigo-50 text-indigo-700"
                    }`}
                  >
                    {t.unit_grouping === "unified" ? "" : "+ "}{lab}
                  </span>
                ))}
                {t.unit_grouping === "unified" && (t.additional_unit_labels || []).length > 0 && (
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-testid="hero-unified-badge">
                    Einheitlich
                  </span>
                )}
              </div>
              {(t.unit_area || t.unit_rooms) && (
                <div className="text-[11px] text-slate-500 mt-1.5">
                  {t.unit_area ? `${t.unit_area} m²` : ""}
                  {t.unit_area && t.unit_rooms ? " · " : ""}
                  {t.unit_rooms ? `${t.unit_rooms} Zi.` : ""}
                </div>
              )}
            </div>
          </div>

          {/* Saldo + Score */}
          <div className="px-6 sm:px-8 py-5">
            <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">Saldo</div>
            <div className={`text-3xl font-semibold tabular-nums tracking-tight ${ledger?.balance < 0 ? "text-rose-700" : "text-emerald-700"}`}>
              {formatEUR(ledger?.balance ?? 0)}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {ledger?.balance < 0 ? "Rückstand" : ledger?.balance > 0 ? "Guthaben" : "Ausgeglichen"}
            </div>
            <SurplusOfferBanner offer={ledger?.surplus_offer} onOpen={() => setSurplusDialogOpen(true)} />
            {!ledger?.surplus_offer && ledger?.surplus_available > 0.005 && (
              <button type="button" onClick={() => setRefundOpen(true)}
                      className="mt-2 text-[12px] text-emerald-800 underline underline-offset-2 hover:text-emerald-900"
                      data-testid="surplus-refund-open">
                Guthaben erstatten
              </button>
            )}
            <SurplusRefundDialog open={refundOpen} onClose={() => setRefundOpen(false)} tenancyId={id} onDone={load} />
            <SurplusOffsetDialog open={surplusDialogOpen} onClose={() => setSurplusDialogOpen(false)}
                                 tenancyId={id} onDone={load} />
            <DunningCorrectionDialog open={dunningFixOpen} onClose={() => setDunningFixOpen(false)}
                                     tenancyId={id} onDone={load} />
            {score && score.score != null && (
              <div className="mt-4 inline-flex items-center gap-2 text-[11px] text-slate-600" data-testid="tenant-score-badge" title={`Mieter-Score ${score.score}/100 · Grade ${score.grade}`}>
                <span className={`text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center text-white ${score.grade === "A" ? "bg-emerald-600" : score.grade === "B" ? "bg-lime-600" : score.grade === "C" ? "bg-amber-500" : score.grade === "D" ? "bg-orange-600" : "bg-rose-700"}`}>{score.grade}</span>
                <span>Mieter-Score <span className="font-semibold tabular-nums text-slate-900">{score.score}</span>/100</span>
              </div>
            )}
            {/* 2026-08 — Ratenzahlungs-Badge */}
            {paymentPlans.length > 0 && (() => {
              const active = paymentPlans.find((p) => p.status === "active") || paymentPlans[0];
              const h = active.health || {};
              const cls = h.status === "on_track" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                       : h.status === "overdue" ? "bg-rose-100 text-rose-800 border-rose-300"
                       : h.status === "completed" ? "bg-slate-200 text-slate-700 border-slate-300"
                       : "bg-slate-100 text-slate-600 border-slate-200";
              const label = h.status === "on_track" ? "planmäßig"
                          : h.status === "overdue" ? "überfällig"
                          : h.status === "completed" ? "abgeschlossen" : "unbekannt";
              return (
                <div
                  className={`mt-2 inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border cursor-pointer ${cls}`}
                  data-testid="payment-plan-badge"
                  onClick={() => document.querySelector('[data-testid="tab-plans"]')?.click()}
                  title={`Ratenzahlung ${h.installments_paid ?? 0}/${active.installments} Raten · ${label}`}
                >
                  <HandCoins size={11} weight="fill" />
                  <span>Ratenzahlung {h.installments_paid ?? 0}/{active.installments} · {label}</span>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* iter142 — Future-Start-Banner: MV startet noch nicht */}
      {(() => {
        const startIso = t.contract_start;
        const handoverIso = t.handover_date;
        const relevant = handoverIso || startIso;
        const today = new Date().toISOString().slice(0, 10);
        const isFuture = relevant && String(relevant) > today
          && t.termination_status !== "cancelled" && t.termination_status !== "ended";
        if (!isFuture) return null;
        const daysUntil = Math.ceil((new Date(relevant) - new Date(today)) / (1000*60*60*24));
        return (
          <div className="bg-sky-50 border border-sky-300 rounded-xl px-5 py-3 shadow-sm flex items-start gap-3" data-testid="tenancy-future-banner">
            <InfoIcon size={20} className="text-sky-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-sky-900">
                Mietverhältnis startet in {daysUntil} Tag{daysUntil !== 1 ? "en" : ""} · {formatDate(relevant)}
              </div>
              <div className="text-xs text-sky-800 mt-0.5">
                Bis dahin sind die Mieter unter ihrer bisherigen Privatadresse zu erreichen.
                {t.update_tenant_address && (
                  <> Die Umstellung der Kontakt-Adressen erfolgt automatisch am Stichtag.</>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* VM-11c — Mahnstopp-Banner */}
      {t.dunning_paused && t.termination_status !== "cancelled" && t.termination_status !== "ended" && (
        <div className="bg-blue-50 border border-blue-300 rounded-xl px-5 py-2.5 shadow-sm flex items-center gap-3 text-sm" data-testid="dunning-paused-banner">
          <InfoIcon size={18} className="text-blue-700 shrink-0" />
          <div className="flex-1">
            <span className="font-semibold text-blue-900">Mahnstopp aktiv</span>
            {t.dunning_paused_reason && <span className="ml-2 text-blue-800">· {t.dunning_paused_reason}</span>}
            {t.dunning_paused_until && (
              <span className="ml-2 text-xs text-blue-700">bis {new Date(t.dunning_paused_until).toLocaleDateString("de-DE")}</span>
            )}
          </div>
          <Button
            size="sm" variant="outline" className="border-blue-300 text-blue-800 hover:bg-blue-100"
            onClick={async () => {
              try {
                await api.post(`/tenancies/${t.id}/dunning-pause`, { paused: false });
                toast.success("Mahnstopp aufgehoben");
                window.location.reload();
              } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
            }}
          >
            Aufheben
          </Button>
        </div>
      )}

      {/* iter144 — Rückstands-Detail: welche Monate sind offen? */}
      <ArrearsBreakdownCard tenancyId={t.id} onChanged={load} refreshKey={reloadTick} />


      {/* VM-11 — Kündigungs-Banner */}
      {t.termination_status === "cancelled" && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl px-5 py-3 shadow-sm flex items-center gap-3" data-testid="tenancy-terminated-banner">
          <Warning size={20} className="text-amber-700 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">
              Gekündigt zum {formatDate(t.termination_effective_date)}
              {t.termination_notice_by && (
                <span className="ml-2 font-normal text-amber-800">
                  · durch {t.termination_notice_by === "tenant" ? "Mieter" : "Vermieter"}
                </span>
              )}
              {t.termination_prorate_last_month && (
                <span className="ml-2 text-xs font-normal text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">
                  letzter Monat anteilig
                </span>
              )}
            </div>
            {t.termination_reason && (
              <div className="text-xs text-amber-800 mt-0.5 italic">„{t.termination_reason}"</div>
            )}
          </div>
          <Button
            size="sm" variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100"
            data-testid="tenancy-close-now-btn"
            onClick={async () => {
              if (!await window._domivusConfirm("Fall jetzt schließen? Einheit wird sofort freigegeben und Sollstellungen ab heute gelöscht.")) return;
              try {
                await api.post(`/tenancies/${t.id}/close-now`);
                toast.success("Mietverhältnis geschlossen · Einheit freigegeben");
                window.location.reload();
              } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
            }}
          >
            Zurückgegeben — jetzt schließen
          </Button>
          <Button
            size="sm" variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100"
            data-testid="tenancy-terminate-edit-btn"
            onClick={() => setOpenTerminateEdit(true)}
          >
            Kündigung ändern
          </Button>
          <Button
            size="sm" variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100"
            data-testid="tenancy-terminate-cancel-btn"
            onClick={async () => {
              try {
                await api.post(`/tenancies/${t.id}/terminate/cancel`);
                toast.success("Kündigung zurückgenommen");
                window.location.reload();
              } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
            }}
          >
            Zurücknehmen
          </Button>
        </div>
      )}
      {t.termination_status === "ended" && (
        <div className="bg-slate-100 border border-slate-300 rounded-xl px-5 py-3 shadow-sm flex items-center gap-3" data-testid="tenancy-ended-banner">
          <CheckCircle size={20} className="text-slate-600 shrink-0" />
          <div className="flex-1 text-sm font-semibold text-slate-700">
            Beendet am {formatDate(t.termination_effective_date || t.contract_end)}
            <span className="ml-2 font-normal text-slate-500 text-xs">Einheit ist wieder verfügbar.</span>
          </div>
          <Button
            size="sm" variant="outline"
            onClick={() => setOpenTerminateEdit(true)}
            data-testid="tenancy-terminate-edit-btn-ended"
          >
            Kündigung ändern
          </Button>
        </div>
      )}

      {/* 2026-08 (iter104) — Kautionswarnung wenn nicht bezahlt */}
      {(() => {
        const dep = Number(t.deposit || 0);
        const paid = t.deposit_status === "paid";
        if (dep <= 0 || paid) return null;
        return (
          <div
            className="bg-rose-50 border border-rose-300 rounded-xl px-4 py-3 flex items-start gap-3 shadow-sm"
            data-testid="deposit-warning-banner"
          >
            <Warning size={20} className="text-rose-600 mt-0.5 flex-shrink-0" weight="fill" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-rose-900">
                Kaution nicht bezahlt {t.deposit_status === "partial" ? "(Teilzahlung)" : ""}
              </div>
              <div className="text-xs text-rose-800 mt-0.5">
                Vereinbarte Kaution: <strong>{formatEUR(dep)}</strong>.
                {t.deposit_status === "partial"
                  ? " Es wurde bisher nur ein Teilbetrag erfasst."
                  : " Es liegt noch keine Zahlungsbestätigung vor."}
              </div>
            </div>
            <Button
              size="sm" variant="outline" className="border-rose-300 text-rose-800 hover:bg-rose-100"
              onClick={() => {
                setActiveTab("deposit");
                // Kurz warten und zum Tab scrollen
                setTimeout(() => {
                  document.querySelector('[data-testid="deposit-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 100);
              }}
              data-testid="deposit-warning-goto"
            >
              Zum Kautionskonto
            </Button>
          </div>
        );
      })()}

      {/* Kompakte Eckdaten-Leiste (4 wichtigste Werte als Inline-Pills) */}
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-3 shadow-sm flex items-center justify-between flex-wrap gap-x-6 gap-y-2 text-sm" data-testid="tenancy-key-facts">
        <KeyFact label="Vertragsbeginn" value={formatDate(t.contract_start)} />
        <KeyFact label="Vertragsende" value={t.contract_end ? formatDate(t.contract_end) : "unbefristet"} />
        <KeyFact label="Kaltmiete" value={<AmendedValue amount={eff("rent_cold", t.rent_cold)} amendment={effAmend("rent_cold")} />} accent />
        {(() => {
          // 2026-08 (iter104) — USt./Warmmiete-Ausweis für Gewerbe / VAT-tenancies
          const vatRate = Number(t.vat_rate || 0);
          const rentCold = eff("rent_cold", t.rent_cold);
          const util = eff("utilities", t.utilities_advance);
          const heat = eff("heating", t.heating_advance);
          const park = eff("parking", t.parking_rent);
          const other = Number(t.other_amount || 0);
          const netTotal = rentCold + util + heat + park + other;
          const vatCold = vatRate > 0 ? rentCold * (vatRate / 100) : 0;
          const vatUtil = vatRate > 0 ? util * (vatRate / 100) : 0;
          const vatHeat = vatRate > 0 ? heat * (vatRate / 100) : 0;
          const vatPark = vatRate > 0 ? park * (vatRate / 100) : 0;
          const vatOther = vatRate > 0 ? other * (vatRate / 100) : 0;
          const vatTotal = vatCold + vatUtil + vatHeat + vatPark + vatOther;
          const warmBrutto = netTotal + vatTotal;
          const anyAmended = !!(effAmend("rent_cold") || effAmend("utilities") || effAmend("heating") || effAmend("parking"));
          return (
            <KeyFact
              label={vatRate > 0 ? "Warmmiete brutto" : "Warmmiete"}
              value={<AmendedValue amount={warmBrutto} amendment={anyAmended ? { effective_date: null, previous_value: null, new_value: warmBrutto, reason: "aus geänderten Positionen aktualisiert" } : null} showArrow={false} />}
              accent
            />
          );
        })()}
        <KeyFact label="Kaution" value={`${formatEUR(t.deposit)} ${t.deposit_status === "paid" ? "✓" : ""}`} />
      </div>

      {/* Details Accordion (Sekundärinfos, weniger Whitespace) */}
      <details className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="tenancy-details-accordion">
        <summary className="px-5 py-3 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-50 select-none">
          Weitere Details ein-/ausblenden
        </summary>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 px-5 py-4 border-t border-slate-100">
          {t.bk_total_only ? (
            <Info
              label="Betriebskosten gesamt"
              value={
                <span>
                  <AmendedValue amount={eff("utilities", t.utilities_advance)} amendment={effAmend("utilities")} />
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                    gesamt · keine Aufschlüsselung
                  </span>
                </span>
              }
            />
          ) : (
            <>
              <Info label="Nebenkosten-VZ" value={<AmendedValue amount={eff("utilities", t.utilities_advance)} amendment={effAmend("utilities")} />} />
              <Info label="Heizkosten-VZ" value={<AmendedValue amount={eff("heating", t.heating_advance)} amendment={effAmend("heating")} />} />
            </>
          )}
          <Info label="Stellplatz" value={<AmendedValue amount={eff("parking", t.parking_rent)} amendment={effAmend("parking")} />} />
          {Number(t.vat_rate || 0) > 0 && (() => {
            // 2026-08 (iter104) — Detaillierter USt-Ausweis (Gewerbe)
            const vatRate = Number(t.vat_rate || 0);
            const rentCold = eff("rent_cold", t.rent_cold);
            const util = eff("utilities", t.utilities_advance);
            const heat = eff("heating", t.heating_advance);
            const park = eff("parking", t.parking_rent);
            const vatCold = rentCold * (vatRate / 100);
            const vatUtil = util * (vatRate / 100);
            const vatHeat = heat * (vatRate / 100);
            const vatPark = park * (vatRate / 100);
            return (
              <>
                <Info label={`USt. Kaltmiete (${vatRate}%)`} value={formatEUR(vatCold)} data-testid="vat-cold" />
                {util > 0 && <Info label={`USt. Nebenkosten (${vatRate}%)`} value={formatEUR(vatUtil)} data-testid="vat-utilities" />}
                {heat > 0 && <Info label={`USt. Heizkosten (${vatRate}%)`} value={formatEUR(vatHeat)} data-testid="vat-heating" />}
                {park > 0 && <Info label={`USt. Stellplatz (${vatRate}%)`} value={formatEUR(vatPark)} data-testid="vat-parking" />}
              </>
            );
          })()}
          <Info label="Zahlungsrhythmus" value={t.payment_rhythm === "monthly" ? "monatlich" : t.payment_rhythm} />
          <Info label="Fälligkeit" value={`zum ${t.due_day}.`} />
          <Info label="Kündigungsfrist" value={`${t.termination_notice_months || 0} Monate`} />
          <Info label="SEPA-Mandat" value={t.sepa_mandate ? "Ja" : "Nein"} />
          {t.handover_date && <Info label="Übergabe" value={`${formatDate(t.handover_date)}${t.handover_time ? `, ${t.handover_time} Uhr` : ""}${t.handover_location ? ` · ${t.handover_location}` : ""}`} data-testid="info-handover" />}
          {t.return_date && <Info label="Rücknahme" value={`${formatDate(t.return_date)}${t.return_time ? `, ${t.return_time} Uhr` : ""}${t.return_location ? ` · ${t.return_location}` : ""}`} data-testid="info-return" />}
        </div>
      </details>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto justify-start gap-1 p-1">
          <TabsTrigger value="ledger" data-testid="tab-ledger">Mietkonto</TabsTrigger>
          <TabsTrigger value="deposit" data-testid="tab-deposit">Kautionskonto</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">Wiederkehrende Sollstellung</TabsTrigger>
          <TabsTrigger value="amendments" data-testid="tab-amendments">Vertragsänderungen</TabsTrigger>
          <TabsTrigger value="mieterhoehung" data-testid="tab-mieterhoehung">Mieterhöhung</TabsTrigger>
          {t.rent_type === "index" && (
            <TabsTrigger value="index" data-testid="tab-index">Indexmiete</TabsTrigger>
          )}
          <TabsTrigger value="contracts" data-testid="tab-contracts">Mietverträge & Nachträge ({docs.filter((d) => d.category === "Verträge").length})</TabsTrigger>
          <TabsTrigger value="protocols" data-testid="tab-protocols">Protokolle</TabsTrigger>
          <TabsTrigger value="damages" data-testid="tab-damages">Schäden</TabsTrigger>
          <TabsTrigger value="cohabitants" data-testid="tab-cohabitants">Mitbewohner</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans">
            Ratenzahlung{paymentPlans.length > 0 ? ` (${paymentPlans.length})` : ""}
          </TabsTrigger>
          <TabsTrigger value="official" data-testid="tab-official">Amtliche Dokumente</TabsTrigger>
          <TabsTrigger value="betriebskosten" data-testid="tab-betriebskosten">Betriebskosten ({docs.filter((d) => d.category === "Betriebskosten" || d.category === "Nebenkosten").length})</TabsTrigger>
          <TabsTrigger value="mappe" data-testid="tab-mappe">Dokumentenmappe</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">Dokumente ({docs.length})</TabsTrigger>
          <TabsTrigger value="communication" data-testid="tab-communication">Kommunikation</TabsTrigger>
        </TabsList>

        <TabsContent value="communication" className="pt-4">
          {t?.id && <CommunicationTab scope={{ tenancy_id: t.id }} ownerTarget={{ tenancy_id: t.id }} title={t.contract_number} />}
        </TabsContent>

        {/* --- LEDGER --- */}
        <TabsContent value="ledger" className="space-y-3 pt-4">
          <InstallmentPlanCard tenancyId={t?.id} currentOutstanding={Math.max(0, -(ledger?.balance || 0))} onChanged={load} />

          {/* iter164c — Zeitraum-Filter (wirkt auch auf PDF-Export) */}
          <div className="flex items-center flex-wrap gap-2 text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="ledger-period-filter">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Zeitraum</span>
            {[
              { k: "all", label: "Alle" },
              { k: "current_year", label: `${new Date().getFullYear()}` },
              { k: "last_6m", label: "Letzte 6 Monate" },
              { k: "custom", label: "Freier Zeitraum" },
            ].map((opt) => (
              <button
                key={opt.k}
                type="button"
                onClick={() => setLedgerPeriod((p) => ({ ...p, preset: opt.k }))}
                className={`px-2.5 py-1 rounded-md border transition-colors ${ledgerPeriod.preset === opt.k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"}`}
                data-testid={`ledger-period-${opt.k}`}
              >
                {opt.label}
              </button>
            ))}
            {ledgerPeriod.preset === "custom" && (
              <>
                <Input
                  type="date" value={ledgerPeriod.from}
                  onChange={(e) => setLedgerPeriod((p) => ({ ...p, from: e.target.value }))}
                  className="h-7 w-36 text-xs"
                  data-testid="ledger-period-from"
                />
                <span className="text-slate-500">bis</span>
                <Input
                  type="date" value={ledgerPeriod.to}
                  onChange={(e) => setLedgerPeriod((p) => ({ ...p, to: e.target.value }))}
                  className="h-7 w-36 text-xs"
                  data-testid="ledger-period-to"
                />
              </>
            )}
            {ledgerRange.from && (
              <span className="ml-2 text-slate-500">
                Zahlungen mit Zielmonat im Zeitraum werden inkludiert
              </span>
            )}
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex gap-3 sm:gap-5 text-xs sm:text-sm bg-white border border-slate-200 rounded-md px-3 sm:px-4 py-2 w-full sm:w-auto justify-between">
              <div><span className="text-slate-500">Soll (Forderung): </span>
                <span className="font-semibold tabular-nums text-rose-700">{formatEUR(ledger?.soll)}</span>
              </div>
              <div className="border-l border-slate-200 pl-5"><span className="text-slate-500">Haben (Zahlung): </span>
                <span className="font-semibold tabular-nums text-emerald-700">{formatEUR(ledger?.ist)}</span>
              </div>
              <div className="border-l border-slate-200 pl-5"><span className="text-slate-500">Saldo: </span>
                <span className={`font-semibold tabular-nums ${ledger?.balance < 0 ? "text-rose-700" : "text-emerald-700"}`}>{formatEUR(ledger?.balance)}</span>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="ghost" className="h-8 gap-1.5 text-slate-600 hover:text-slate-900" onClick={() => load()} data-testid="reload-tenancy-btn" title="Mietkonto neu laden (aktuelle Daten aus der Datenbank holen)">
                <ArrowsClockwise size={14} /> Neu laden
              </Button>
              <Button size="sm" variant="outline" className="h-8 gap-1.5"
                onClick={async () => {
                  try {
                    // iter159w — Kontoauszug PDF herunterladen (iter164c: mit Zeitraum)
                    const params = {};
                    if (ledgerRange.from) params.from_date = ledgerRange.from;
                    if (ledgerRange.to) params.to_date = ledgerRange.to;
                    const res = await api.get(`/tenancies/${id}/account-statement.pdf`, { responseType: "blob", params });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a");
                    a.href = url;
                    const suffix = ledgerPeriod.preset === "current_year" ? `_${new Date().getFullYear()}`
                                 : ledgerRange.from ? `_${ledgerRange.from}_bis_${ledgerRange.to || "offen"}` : "";
                    a.download = `Kontoauszug_${t?.contract_number || id}${suffix}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success("Kontoauszug heruntergeladen");
                  } catch (e) {
                    toast.error("Kontoauszug konnte nicht erzeugt werden");
                  }
                }}
                data-testid="account-statement-pdf-btn">
                <FileText size={14} /> Kontoauszug PDF
              </Button>
              <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={generateSoll} data-testid="generate-soll-btn">
                <Calculator size={14} /> Nächsten Monat erzeugen
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="h-8 gap-1.5" data-testid="tenancy-maintenance-menu">
                    <Calculator size={14} /> Wartung <span className="text-xs">▾</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <DropdownMenuItem
                    onClick={async () => {
                      try {
                        const { data } = await api.get(`/tenancies/${id}/soll-gaps`);
                        if (!data.gaps || data.gaps.length === 0) {
                          toast.success(`Keine fehlenden Sollstellungen (${data.checked} Perioden geprüft)`);
                        } else {
                          const first = data.gaps.slice(0, 5).map((g) => `${g.month} · ${g.category} (${g.expected_amount} €)`).join("\n");
                          const more = data.gaps.length > 5 ? `\n… und ${data.gaps.length - 5} weitere` : "";
                          if (window.confirm(`${data.gaps.length} fehlende Sollstellungen gefunden:\n\n${first}${more}\n\nJetzt via „Alles neu ab Vertragsbeginn" reparieren?`)) {
                            regenerateSollFull();
                          }
                        }
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Diagnose fehlgeschlagen");
                      }
                    }}
                    data-testid="soll-gaps-btn"
                    className="gap-2 cursor-pointer"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-teal-800">Fehlende Sollstellungen prüfen</span>
                      <span className="text-[11px] text-slate-500">Findet Monate, für die laut Regel Miete/NK/Heiz fällig wäre, aber keine Sollstellung existiert.</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setDunningFixOpen(true)} data-testid="dunning-correction-menu" className="gap-2 cursor-pointer">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-rose-800">Mahnungen prüfen & korrigieren</span>
                      <span className="text-[11px] text-slate-500">Salden- und Zuordnungsprüfung; fehlerhafte Mahngebühren/Zinsen werden per Stornobuchung korrigiert.</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setSurplusDialogOpen(true)}
                    data-testid="reallocate-account-btn"
                    className="gap-2 cursor-pointer"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-emerald-800">Guthaben verrechnen</span>
                      <span className="text-[11px] text-slate-500">Verrechnet Guthaben mit fälligen offenen Posten (ältester Monat zuerst, Kaltmiete zuerst) und informiert den Mieter.</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setRefundOpen(true)} data-testid="surplus-refund-menu" className="gap-2 cursor-pointer">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-emerald-800">Guthaben erstatten</span>
                      <span className="text-[11px] text-slate-500">Restguthaben per SEPA-Überweisung an den Mieter erstatten (Buchung + Zahlungsauftrag).</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={async () => {
                      const next = (t?.surplus_offset_mode || "manual") === "auto" ? "manual" : "auto";
                      try {
                        const { data } = await api.patch(`/tenancies/${id}/surplus-offset-mode`, { mode: next });
                        toast.success(next === "auto"
                          ? `Guthaben-Verrechnung: automatisch${data.applied?.created ? ` · ${formatEUR(data.applied.amount)} verrechnet` : ""}`
                          : "Guthaben-Verrechnung: manuell");
                        setT((x) => ({ ...x, surplus_offset_mode: next }));
                        load();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Umstellen fehlgeschlagen");
                      }
                    }}
                    data-testid="surplus-mode-toggle"
                    className="gap-2 cursor-pointer"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-slate-800">
                        Guthaben-Verrechnung: {(t?.surplus_offset_mode || "manual") === "auto" ? "Automatisch" : "Manuell"}
                      </span>
                      <span className="text-[11px] text-slate-500">
                        Klicken, um auf {(t?.surplus_offset_mode || "manual") === "auto" ? "manuell (nur per Klick)" : "automatisch (sofort + Mitteilung an Mieter)"} umzustellen.
                      </span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={regenerateSoll} data-testid="regenerate-soll-btn" className="gap-2 cursor-pointer">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-amber-800">Fälligkeiten neu berechnen</span>
                      <span className="text-[11px] text-slate-500">Ab heute: Soll für kommende Monate neu erzeugen (historische Werte bleiben unberührt).</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={regenerateSollFull} data-testid="regenerate-soll-full-btn" className="gap-2 cursor-pointer">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-rose-800">Alles neu ab Vertragsbeginn</span>
                      <span className="text-[11px] text-slate-500">Löscht ALLE Soll-Buchungen (auch historische) und erzeugt sie neu inkl. anteiligem Startmonat.</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={regenerateSollPreMgmt} data-testid="regenerate-soll-premgmt-btn" className="gap-2 cursor-pointer">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-indigo-800">Sollstellungen vor Verwalterbeginn erzeugen</span>
                      <span className="text-[11px] text-slate-500">Für Altverträge oder übernommene Rückstände: erzeugt Soll auch für Zeiträume vor Ihrem Verwalterbeginn.</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={revertAllPreOwnership} data-testid="revert-preownership-btn" className="gap-2 cursor-pointer">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-slate-800">Vor-Eigentümer-Zuweisungen prüfen…</span>
                      <span className="text-[11px] text-slate-500">Öffnet eine Übersicht aller als Vor-Eigentümer markierten Buchungen — gezielte Auswahl zum Zurücksetzen.</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={resetAccount} data-testid="reset-account-btn" className="gap-2 cursor-pointer">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-rose-900">Mietkonto komplett zurücksetzen</span>
                      <span className="text-[11px] text-slate-500">Löscht ALLE Soll UND Ist-Buchungen. Danach müssen Zahlungen und Fälligkeiten neu aufgebaut werden.</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Dialog open={openPosting} onOpenChange={setOpenPosting}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-slate-900 hover:bg-slate-800 h-8 gap-1.5" data-testid="new-posting-btn">
                    <Plus size={14} /> Buchung erfassen
                  </Button>
                </DialogTrigger>
                <PostingDialog tenancyId={id} onSaved={() => { setOpenPosting(false); load(); }} />
              </Dialog>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm">
            {(ledger?.hidden_pre_ownership_count > 0 || includePreOwnership) && (
              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs"
                   data-testid="pre-ownership-banner">
                <div className="text-slate-600">
                  {includePreOwnership ? (
                    <>Vor-Eigentümer-Buchungen sind sichtbar{ledger?.owned_since && (<span className="ml-1 text-slate-500">(vor {new Date(ledger.owned_since).toLocaleDateString("de-DE")})</span>)}</>
                  ) : (
                    <>🕰 {ledger?.hidden_pre_ownership_count} Buchung{ledger?.hidden_pre_ownership_count === 1 ? "" : "en"} vor Ihrem Eigentums-Beginn{ledger?.owned_since && (<span className="ml-1 text-slate-500">({new Date(ledger.owned_since).toLocaleDateString("de-DE")})</span>)} werden ausgeblendet</>
                  )}
                </div>
                <button type="button"
                        className="text-xs text-slate-700 hover:text-slate-900 underline"
                        onClick={() => setIncludePreOwnership(!includePreOwnership)}
                        data-testid="toggle-pre-ownership">
                  {includePreOwnership ? "Ausblenden" : "Trotzdem anzeigen"}
                </button>
              </div>
            )}
            <LedgerMobileList ledger={ledger} tenancyId={id} selected={selectedPostings}
                              onToggle={toggleSelectPosting} onSelectAll={selectAllVisible} onClear={clearSelection}
                              onDelete={deletePosting} onChanged={load} />
            <table className="w-full text-sm hidden md:table">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <Th className="w-9 pl-3">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                      checked={
                        (ledger?.postings || []).length > 0 &&
                        selectedPostings.size === (ledger?.postings || []).length
                      }
                      ref={(el) => {
                        if (el) {
                          const total = (ledger?.postings || []).length;
                          el.indeterminate = selectedPostings.size > 0 && selectedPostings.size < total;
                        }
                      }}
                      onChange={(e) => {
                        if (e.target.checked) selectAllVisible();
                        else clearSelection();
                      }}
                      data-testid="ledger-select-all"
                      title="Alle sichtbaren auswählen"
                    />
                  </Th>
                  <Th>Datum</Th>
                  <Th>Kategorie</Th>
                  <Th>Buchungstext</Th>
                  <Th className="text-right">Soll €</Th>
                  <Th className="text-right">Haben €</Th>
                  <Th className="text-right">Saldo €</Th>
                  <Th className="w-12"></Th>
                </tr>
              </thead>
              <tbody>
                {(ledger?.postings || []).length === 0 && (
                  <tr><td colSpan={8} className="p-8 text-center text-slate-500">Keine Buchungen vorhanden.</td></tr>
                )}
                {(ledger?.postings || []).map((p, idx, arr) => {
                  return (
                  <React.Fragment key={p.id}>
                  <tr className={
                    `border-t border-slate-100 hover:bg-slate-50/70 ` +
                    (p.pre_ownership ? "bg-slate-100/70 text-slate-500 italic " : "") +
                    (p.is_auto_settlement ? "bg-amber-50/60" : "") +
                    (selectedPostings.has(p.id) ? " bg-teal-50/60" : "")
                  }>
                    <Td className="pl-3">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                        checked={selectedPostings.has(p.id)}
                        onChange={() => toggleSelectPosting(p.id)}
                        data-testid={`ledger-select-${p.id}`}
                      />
                    </Td>
                    <Td className="tabular-nums whitespace-nowrap">
                      {formatDate(p.posting_date)}
                      {p.pre_ownership && (
                        <span className="ml-1 inline-flex items-center rounded border border-slate-300 bg-slate-100 px-1 py-0.5 text-[9px] font-medium text-slate-600"
                              title="Buchung liegt vor dem Eigentums-Beginn. Wird dem Vor-Eigentümer zugerechnet.">
                          🕰 VOR-EIG.
                        </span>
                      )}
                    </Td>
                    <Td>
                      <PostingCategoryLabel p={p} />
                      {p.type === "ist" && !p.linked_installment_plan_id && p.category !== "surplus" && <PeriodBadge p={p} tenancyId={id} onChanged={load} />}
                      {p.is_auto_settlement && (
                        <span className="ml-1.5 inline-flex items-center rounded border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                              title="Automatisch generierter Vor-Verwalter-Ausgleich. Wird durch echte Bankbuchungen ersetzt.">
                          AUTO
                        </span>
                      )}
                      {p.split_info && (
                        <span
                          className="ml-1.5 inline-flex items-center text-[9px] font-medium text-slate-400 hover:text-slate-600 cursor-help"
                          title={
                            `Aufgeteilt aus ${p.split_info.total_amount.toLocaleString("de-DE", { minimumFractionDigits: 2 })} € ${p.split_info.source === "manual" ? "manueller" : "Bank-"}Zahlung vom ${new Date(p.split_info.booking_date).toLocaleDateString("de-DE")}` +
                            ` · Diese Zahlung wurde auf ${p.split_info.split_count} Sollpositionen aufgeteilt` +
                            (p.split_info.purpose ? `\nVerwendungszweck: ${p.split_info.purpose}` : "")
                          }
                          data-testid={`split-info-${p.id}`}
                        >
                          ⇢split
                        </span>
                      )}
                      {p.is_storno && (
                        <span className="ml-1.5 inline-flex items-center rounded border border-rose-300 bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium text-rose-800"
                              title={p.storno_reason || "Stornobuchung"} data-testid={`storno-chip-${p.id}`}>
                          STORNO
                        </span>
                      )}
                      {p.storniert && (
                        <span className="ml-1.5 inline-flex items-center rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 line-through"
                              title={p.storno_reason || "Storniert"} data-testid={`storniert-chip-${p.id}`}>
                          storniert
                        </span>
                      )}
                      {p.is_surplus_offset && (
                        <span className="ml-1.5 inline-flex items-center rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800"
                              title="Interne Verrechnung aus dem Überschuss-Konto — Gesamtsaldo bleibt unverändert"
                              data-testid={`surplus-offset-chip-${p.id}`}>
                          Verrechnung aus Guthaben
                        </span>
                      )}
                    </Td>
                    <Td className="text-slate-600 text-xs whitespace-normal break-words max-w-[280px]">
                      {p.reference || "—"}
                      {(p.is_storno || p.storniert) && p.storno_reason && (
                        <div className="text-[10px] text-rose-700 mt-0.5 whitespace-normal break-words" data-testid={`storno-reason-${p.id}`}>
                          Grund: {p.storno_reason}
                        </div>
                      )}
                      {p.is_auto_settlement && (
                        <div className="text-[10px] text-amber-700 mt-0.5 italic whitespace-normal break-words">
                          Automatisch erzeugter Ausgleich — wird durch echte Kontoauszüge ersetzt
                        </div>
                      )}
                      {p.arrears_allocation && p.arrears_allocation.length > 0 && (
                        <div className="text-[10px] text-emerald-700 mt-0.5 whitespace-normal break-words leading-snug"
                             data-testid={`posting-alloc-${p.id}`}>
                          {p.arrears_allocation.map((a, i) => (
                            <div key={i}>→ {formatEUR(a.amount)} {
                              {rent_cold: "Kaltmiete", utilities: "Nebenkosten",
                               heating: "Heizkosten", parking: "Stellplatz",
                               bka: "BKA-Nachzahlung"}[a.category] || a.category
                            } {a.period}</div>
                          ))}
                        </div>
                      )}
                      {p.original_snapshot && (
                        <div
                          className="mt-1 inline-flex items-start gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-900"
                          title={`Original: ${Number(p.original_snapshot.amount ?? 0).toLocaleString("de-DE",{minimumFractionDigits:2})} € · Fällig ${p.original_snapshot.posting_date || "—"} · Grund: ${p.original_snapshot.reference || "manuell angepasst"}`}
                          data-testid={`posting-snapshot-${p.id}`}
                        >
                          <span className="font-semibold">Original:</span>
                          {Number(p.original_snapshot.amount ?? 0).toLocaleString("de-DE",{minimumFractionDigits:2})} €
                          {p.updated_at && (
                            <span className="opacity-70">· geändert {formatDate(p.updated_at)}</span>
                          )}
                        </div>
                      )}
                    </Td>
                    <Td className="text-right tabular-nums text-rose-700 font-medium">
                      {p.type === "soll" ? formatEUR(p.soll) : ""}
                    </Td>
                    <Td className="text-right tabular-nums text-emerald-700 font-medium">
                      {p.type === "ist" ? formatEUR(p.haben) : ""}
                    </Td>
                    <Td className={`text-right tabular-nums font-semibold ${p.saldo < 0 ? "text-rose-700" : "text-slate-800"}`}>
                      {formatEUR(p.saldo)}
                    </Td>
                    <Td>
                      <div className="flex items-center justify-end">
                        <PostingActionsMenu
                          p={p}
                          tenancyId={id}
                          onDeleted={() => deletePosting(p.id)}
                          onChanged={load}
                        />
                      </div>
                    </Td>
                  </tr>
                  </React.Fragment>
                );})}
              </tbody>
              {(ledger?.postings || []).length > 0 && (
                <tfoot className="bg-slate-50 border-t border-slate-200">
                  <tr>
                    <Td colSpan={4} className="font-semibold text-slate-700">Summe</Td>
                    <Td className="text-right tabular-nums font-semibold text-rose-700">{formatEUR(ledger?.soll)}</Td>
                    <Td className="text-right tabular-nums font-semibold text-emerald-700">{formatEUR(ledger?.ist)}</Td>
                    <Td className={`text-right tabular-nums font-bold ${ledger?.balance < 0 ? "text-rose-700" : "text-emerald-700"}`}>{formatEUR(ledger?.balance)}</Td>
                    <Td></Td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {mgmtStart && (
            <div
              className="mt-1 px-3 text-[10.5px] text-slate-400 leading-snug flex items-center gap-1.5"
              data-testid="ledger-cutoff-note"
              title="Vor diesem Datum werden keine Sollstellungen geführt."
            >
              <span className="inline-block h-px w-4 bg-slate-300" />
              {mgmtStart === ownershipStart
                ? <>Sollstellungen ab <span className="font-medium text-slate-500">{new Date(mgmtStart).toLocaleDateString("de-DE")}</span> · Eigentumsbeginn</>
                : (ownershipStart && mgmtStart > ownershipStart)
                  ? <>Sollstellungen ab <span className="font-medium text-slate-500">{new Date(mgmtStart).toLocaleDateString("de-DE")}</span> · Verwaltungsbeginn (Eigentum ab {new Date(ownershipStart).toLocaleDateString("de-DE")})</>
                  : <>Sollstellungen ab <span className="font-medium text-slate-500">{new Date(mgmtStart).toLocaleDateString("de-DE")}</span></>
              }
            </div>
          )}
          {selectedPostings.size > 0 && (
            <div
              className="sticky bottom-3 z-30 mx-auto max-w-3xl flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900 text-slate-50 px-4 py-2.5 shadow-lg"
              data-testid="ledger-bulk-bar"
            >
              <div className="text-sm">
                <span className="font-semibold">{selectedPostings.size}</span> Buchung{selectedPostings.size === 1 ? "" : "en"} ausgewählt
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5 bg-transparent border-slate-500 text-slate-100 hover:bg-slate-800"
                  onClick={bulkUnsplitSelected}
                  data-testid="ledger-bulk-unsplit"
                  title="Gesplittete Buchungen je Gruppe zu einer Buchung zusammenführen"
                >
                  Splits zusammenführen
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5 bg-transparent border-slate-500 text-slate-100 hover:bg-slate-800"
                  onClick={bulkUnlinkSelected}
                  data-testid="ledger-bulk-unlink"
                  title="Verknüpfung zur Bankbuchung aufheben (inkl. Split-Gruppen)"
                >
                  Verknüpfung aufheben
                </Button>
                <Button
                  size="sm"
                  className="h-8 gap-1.5 bg-rose-700 hover:bg-rose-800 text-white"
                  onClick={bulkDeleteSelected}
                  data-testid="ledger-bulk-delete"
                >
                  Löschen
                </Button>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-xs text-slate-400 hover:text-slate-100 px-1"
                  data-testid="ledger-bulk-clear"
                  title="Auswahl aufheben"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* --- DEPOSIT (Kautionskonto) — Sprint 5h --- */}
        <TabsContent value="deposit" className="space-y-3 pt-4">
          {(t?.status === "ended" || t?.status === "terminated") && (
            <EndOfTenancyActions tenancy={t} onChanged={load} />
          )}
          <DepositAccountPanel tenancyId={id} tenancyType={t?.tenancy_type}
                               tenancyEnded={t?.status === "ended" || t?.termination_status === "ended"}
                               onChanged={load} />
        </TabsContent>

        {/* --- DAMAGES (iter160j) --- */}
        <TabsContent value="damages" className="space-y-3 pt-4">
          <TenancyDamagesTab tenancyId={id} propertyId={t?.property_id} unitId={t?.unit_id} />
        </TabsContent>

        {/* --- RULES --- */}
        <TabsContent value="rules" className="space-y-3 pt-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600 max-w-2xl">
              Wiederkehrende Sollstellungen für einen frei wählbaren Zeitraum.
              Frequenz, Start- und Enddatum, Betrag und Position frei definierbar.
              Über „Anwenden" werden alle noch fehlenden Sollstellungen bis heute erzeugt.
            </div>
            <Dialog open={openRule} onOpenChange={setOpenRule}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800 h-8 gap-1.5" data-testid="new-rule-btn">
                  <Repeat size={14} /> Neue Regel
                </Button>
              </DialogTrigger>
              <RuleDialog tenancyId={id} tenancy={t} onSaved={() => { setOpenRule(false); load(); }} />
            </Dialog>
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <Th>Position</Th>
                  <Th>Frequenz</Th>
                  <Th>Zeitraum</Th>
                  <Th>Fälligkeit</Th>
                  <Th className="text-right">Betrag</Th>
                  <Th>Notiz</Th>
                  <Th className="w-40 text-right">Aktion</Th>
                </tr>
              </thead>
              <tbody>
                {rules.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-slate-500">Noch keine wiederkehrende Sollstellung angelegt.</td></tr>
                )}
                {rules.map((r) => {
                  // iter163b — Abgelaufene Regel visuell durchstreichen
                  const today = new Date().toISOString().slice(0, 10);
                  const isExpired = !!(r.end_date && r.end_date < today);
                  // iter159n — Amendment-Badge (Original vs. geändert)
                  const ai = r.amendment_info;
                  let amBadge = null;
                  let amBorderCls = "";
                  if (ai) {
                    const catShort = ({ rent_change: "MIETE", utilities_change: "NK", heating_change: "HK", parking_change: "STELLPL." })[ai.amendment_type] || "GEÄNDERT";
                    const prev = ai.previous_value;
                    const nv = ai.new_value;
                    const isNew = prev === null || prev === undefined;
                    const isUp = !isNew && Number(nv) > Number(prev);
                    const isDown = !isNew && Number(nv) < Number(prev);
                    const arrow = isNew ? "NEU" : (isUp ? "↑" : (isDown ? "↓" : "="));
                    const colorCls = isNew
                      ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                      : isUp
                        ? "border-blue-300 bg-blue-50 text-blue-700"
                        : isDown
                          ? "border-rose-300 bg-rose-50 text-rose-700"
                          : "border-slate-300 bg-slate-50 text-slate-700";
                    amBorderCls = isNew
                      ? "border-l-2 border-l-emerald-400 "
                      : isUp
                        ? "border-l-2 border-l-blue-400 "
                        : isDown
                          ? "border-l-2 border-l-rose-400 "
                          : "border-l-2 border-l-slate-400 ";
                    const effFmt = formatDate(ai.effective_date);
                    const prevFmt = isNew ? "—" : Number(prev).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
                    const nvFmt = Number(nv || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
                    const tip = `Vertragsänderung ab ${effFmt}: ${prevFmt} → ${nvFmt}${ai.reason ? " · " + ai.reason : ""}`;
                    amBadge = (
                      <span
                        className={`ml-1.5 inline-flex items-center gap-0.5 rounded border ${colorCls} px-1.5 py-0.5 text-[10px] font-medium cursor-help`}
                        title={tip}
                        data-testid={`rule-amendment-badge-${r.id}`}
                      >
                        {catShort} {arrow}
                      </span>
                    );
                  }
                  return (
                  <tr key={r.id} className={`border-t border-slate-100 hover:bg-slate-50/70 ${amBorderCls} ${isExpired ? "text-slate-400" : ""}`}>
                    <Td className={`font-medium ${isExpired ? "line-through" : ""}`}>
                      {POSTING_CATEGORIES[r.category] || r.category}
                      {amBadge}
                      {isExpired && (
                        <span
                          className="ml-1.5 inline-flex items-center rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 normal-case"
                          title="Diese Regel wurde durch eine spätere Vertragsänderung abgelöst und ist nicht mehr aktiv."
                          data-testid={`rule-expired-badge-${r.id}`}
                        >
                          abgelaufen
                        </span>
                      )}
                    </Td>
                    <Td className={isExpired ? "line-through" : ""}>{r.frequency === "monthly" ? "monatlich" : r.frequency === "quarterly" ? "quartalsweise" : "jährlich"}</Td>
                    <Td className={`tabular-nums ${isExpired ? "line-through" : ""}`}>{formatDate(r.start_date)} – {r.end_date ? formatDate(r.end_date) : "offen"}</Td>
                    <Td className={isExpired ? "line-through" : ""}>zum {r.due_day}.</Td>
                    <Td className={`text-right tabular-nums font-semibold ${isExpired ? "line-through" : ""}`}>{formatEUR(r.amount)}</Td>
                    <Td className={`text-slate-600 text-xs ${isExpired ? "line-through" : ""}`}>{r.note || "—"}</Td>
                    <Td className="text-right">
                      <Button size="sm" variant="outline" className="h-7 mr-1" onClick={() => applyRule(r.id)} data-testid={`apply-rule-${r.id}`}>
                        Anwenden
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-rose-600 hover:text-rose-700" onClick={() => deleteRule(r.id)}>
                        <Trash size={14} />
                      </Button>
                    </Td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* --- INDEXMIETE --- */}
        {t.rent_type === "index" && (
          <TabsContent value="index" className="space-y-3 pt-4">
            <IndexRentSection tenancy={t} onApplied={load} />
          </TabsContent>
        )}

        {/* --- VERTRAGSÄNDERUNGEN (P2) --- */}
        <TabsContent value="amendments" className="space-y-3 pt-4">
          <TenancyAmendments tenancyId={id} onChanged={load} />
        </TabsContent>

        {/* --- MIETVERTRÄGE & NACHTRÄGE --- */}
        <TabsContent value="contracts" className="space-y-3 pt-4" data-testid="contracts-tab-content">
          <div className="bg-emerald-50/50 border border-emerald-200 rounded-md p-3 text-sm text-slate-700 flex items-start gap-2">
            <FileArrowUp size={18} className="text-emerald-700 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-emerald-900">Mietverträge & Nachträge</div>
              <div className="text-xs text-slate-600 mt-0.5">
                Hier erscheinen alle Dokumente, die diesem Mietverhältnis mit Kategorie <span className="font-mono bg-white px-1 rounded">„Verträge"</span> zugeordnet sind. Sie sind weiterhin auch im Haupt-Dokumente-Modul auffindbar.
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2 shrink-0">
              <input type="file" id="contract-upload-input" className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const fd = new FormData();
                  fd.append("file", f);
                  fd.append("title", f.name);
                  fd.append("category", "Verträge");
                  fd.append("direction", "internal");
                  fd.append("linked_tenancy_id", id);
                  if (t.property_id) fd.append("linked_property_id", t.property_id);
                  if (t.unit_id) fd.append("linked_unit_id", t.unit_id);
                  try {
                    await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    toast.success("Vertragsdokument hochgeladen");
                    e.target.value = "";
                    load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
                  }
                }} />
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 gap-1.5 whitespace-nowrap"
                onClick={() => document.getElementById("contract-upload-input")?.click()}
                data-testid="upload-contract-btn">
                <FileArrowUp size={14} /> Vertrag hochladen
              </Button>
              <Button size="sm" variant="outline" className="h-9 gap-1.5 whitespace-nowrap"
                onClick={() => setOpenGenerateContract(true)}
                data-testid="generate-contract-btn">
                <Briefcase size={14} /> Mietvertrag aus Vorlage
              </Button>
              <MailboxScanPicker allKinds endpoint={(mid) => `/documents/tenancy/${id}/contracts/from-mailbox/${mid}`} onPicked={() => load()}
                title="Vertragsdokument aus dem Postfach übernehmen" label="Aus Postfach" testId="contract-from-mailbox" />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <Th>Vertragsdokument</Th>
                  <Th>Erstellt</Th>
                  <Th>Verfasser</Th>
                  <Th className="text-right">Größe</Th>
                  <Th className="w-24"></Th>
                </tr>
              </thead>
              <tbody data-testid="contracts-tbody">
                {docs.filter((d) => d.category === "Verträge").length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-500">
                    Noch keine Verträge oder Nachträge hinterlegt. Lade ein PDF/Word-Dokument mit Kategorie „Verträge" hoch.
                  </td></tr>
                )}
                {docs.filter((d) => d.category === "Verträge").map((d) => (
                  <tr key={d.id} className="border-t border-slate-100 hover:bg-emerald-50/30" data-testid={`contract-row-${d.id}`}>
                    <Td>
                      <div className="font-medium text-slate-900">{d.title}</div>
                      <div className="text-[11px] text-slate-500">{d.original_filename}{d.version > 1 ? ` · v${d.version}` : ""}</div>
                      {d.notes && <div className="text-[11px] text-slate-600 italic mt-0.5">{d.notes}</div>}
                    </Td>
                    <Td className="tabular-nums text-xs text-slate-500">{formatDate(docDate(d))}</Td>
                    <Td className="text-xs text-slate-600">
                      {d.author_name || "—"}{d.author_code ? ` · ${d.author_code}` : ""}
                    </Td>
                    <Td className="text-right text-xs text-slate-500">{((d.size || 0) / 1024).toFixed(0)} KB</Td>
                    <Td className="text-right">
                      <DocMetaButton doc={d} onSaved={load} />
                      <Button size="sm" variant="ghost" onClick={() => setPreviewDoc(d)} data-testid={`preview-contract-${d.id}`} title="Vorschau">
                        <Eye size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadDoc(d)} data-testid={`download-contract-${d.id}`}>
                        <Download size={14} className="mr-1" /> Öffnen
                      </Button>
                      <DocRowActions doc={d} onChanged={load} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* --- DOKUMENTENMAPPE (VM-12 Phase B) --- */}
        <TabsContent value="mappe" className="space-y-3 pt-4" data-testid="mappe-tab-content">
          <DokumentenmappeTab tenancyId={id} />
        </TabsContent>

        {/* --- PROTOKOLLE (2026-08) --- */}
        <TabsContent value="protocols" className="space-y-3 pt-4" data-testid="protocols-tab-content">
          <ProtocolsPanel tenancyId={id} />
        </TabsContent>

        {/* --- MITBEWOHNER (2026-08 iter103) --- */}
        <TabsContent value="cohabitants" className="space-y-3 pt-4" data-testid="cohabitants-tab-content">
          <CohabitantsPanel tenancyId={id} />
        </TabsContent>

        {/* --- RATENZAHLUNGSVEREINBARUNGEN (iter159g fix) --- */}
        {/* Zeigt ALLE Ratenpläne (aktiv, abgeschlossen, storniert) aus der
            installment_plans-Collection — konsistent zur InstallmentPlanCard,
            die auf der Startseite nur die aktive Rate anzeigt. */}
        <TabsContent value="plans" className="space-y-3 pt-4" data-testid="plans-tab-content">
          <div className="text-sm text-slate-600 mb-2">
            Alle Ratenzahlungsvereinbarungen für dieses Mietverhältnis (aktiv, abgeschlossen und storniert).
          </div>
          <InstallmentPlanCard
            tenancyId={t?.id}
            currentOutstanding={Math.max(0, -(ledger?.balance || 0))}
            showAll={true}
            onChanged={load}
          />
        </TabsContent>

        {/* --- AMTLICHE DOKUMENTE (2026-08 iter103) --- */}
        <TabsContent value="mieterhoehung" className="pt-4" data-testid="mieterhoehung-tab-content">
          <RentIncreasePanel tenancyId={id}
            onCreateLetter={() => { setOfficialPreselect("mieterhoehung_558"); setActiveTab("official"); }}
            onOpenEditor={(d) => { setPendingMsgSubject(d.subject || ""); setPendingMsgExtra({ sub: d.sub_subject || "", category: d.category || "", follow: d.follow_up_date || "", rid: d.request_id, draft: d.draft_id, myref: d.my_reference || "" }); setPendingMsgBody(d.draft_id ? "" : d.body_html); setPendingMsgAttachments(d.attachment_document_ids); setOpenMsg(true); load(); }}
            onPrinted={load} />
        </TabsContent>
        <TabsContent value="official" className="space-y-3 pt-4" data-testid="official-tab-content">
          {/* iter167 · Kündigungs-Assistent Quick-Action */}
          <div className="flex items-center justify-between bg-teal-50 border border-teal-200 rounded-md px-3 py-2">
            <div className="text-sm text-teal-900">
              <b>Kündigungs-Assistent</b> — Vermieterkündigung mit BGB-Textbausteinen und
              automatischer Fristberechnung.
            </div>
            <Button
              className="bg-teal-600 hover:bg-teal-700 text-white h-8 text-xs"
              onClick={() => setOpenKuendigungAssistant(true)}
              data-testid="open-kuendigung-assistant"
            >
              Assistent starten
            </Button>
          </div>
          <OfficialDocumentsPanel
            tenancyId={id}
            preselectTemplateId={officialPreselect}
            onGenerated={load}
            onAttachToEditor={(att) => {
              // Öffnet Editor-Dialog mit dem eben erzeugten Amtl. Dok. als vorausgewähltem Anhang
              setPendingMsgAttachments([att.document_id]);
              setOpenMsg(true);
            }}
          />
          <KuendigungAssistant
            open={openKuendigungAssistant}
            onClose={() => setOpenKuendigungAssistant(false)}
            tenancyId={id}
            onCreated={load}
          />
        </TabsContent>


        {/* --- DOCUMENTS --- */}
        <TabsContent value="docs" className="space-y-3 pt-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Dokumente werden direkt diesem Mietverhältnis zugeordnet (Mietvertrag, Übergabeprotokoll, Korrespondenz, …).
            </div>
            <div>
              <input type="file" ref={fileInputRef} className="hidden" onChange={uploadDoc} data-testid="doc-upload-input" />
              <Button size="sm" className="bg-slate-900 hover:bg-slate-800 h-8 gap-1.5" onClick={() => fileInputRef.current?.click()} data-testid="upload-doc-btn">
                <FileArrowUp size={14} /> Datei hochladen
              </Button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <Th>Dokument</Th>
                  <Th>Kategorie</Th>
                  <Th>Erstellt</Th>
                  <Th className="text-right">Größe</Th>
                  <Th className="w-24"></Th>
                </tr>
              </thead>
              <tbody>
                {docs.length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-500">Noch keine Dokumente vorhanden.</td></tr>
                )}
                {docs.map((d) => (
                  <tr key={d.id} className={`border-t border-slate-100 hover:bg-slate-50/70 ${d.notify_owner ? "bg-amber-50/40" : ""}`}>
                    <Td className="font-medium">
                      <div className="flex items-center gap-1.5">
                        {d.notify_owner && (
                          <span title="Zur Kenntnis für Eigentümer" className="inline-flex items-center gap-0.5 text-[10px] uppercase font-semibold text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full">
                            <BellRinging size={9} weight="fill" /> ZK
                          </span>
                        )}
                        {d.title}
                        {d.voided && <span className="ml-1.5 text-[10px] font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-1.5 py-0.5" title={d.void_reason} data-testid={`tenancy-doc-voided-badge-${d.id}`}>storniert / gegenstandslos</span>}
                      </div>
                    </Td>
                    <Td>
                      <Select value={d.category || "Sonstiges"} onValueChange={(v) => changeDocCategory(d, v)}>
                        <SelectTrigger className="h-7 w-44 text-xs" data-testid={`doc-cat-${d.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_CATEGORIES.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Td>
                    <Td className="tabular-nums text-xs text-slate-500">{formatDate(docDate(d))}</Td>
                    <Td className="text-right text-xs text-slate-500">{((d.size || 0) / 1024).toFixed(0)} KB</Td>
                    <Td className="text-right">
                      <DocMetaButton doc={d} onSaved={load} />
                      <Button size="sm" variant="ghost" onClick={() => setPreviewDoc(d)} data-testid={`preview-doc-${d.id}`} title="Vorschau">
                        <Eye size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadDoc(d)} data-testid={`download-doc-${d.id}`} title="Herunterladen">
                        <Download size={14} />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => toggleNotifyOwner(d)}
                        data-testid={`notify-owner-${d.id}`}
                        title={d.notify_owner ? "Kenntnisnahme-Markierung entfernen" : "Zur Kenntnis für Eigentümer markieren"}
                        className={d.notify_owner ? "text-amber-600 hover:bg-amber-50" : "text-slate-400 hover:text-amber-600 hover:bg-amber-50"}
                      >
                        <BellRinging size={14} weight={d.notify_owner ? "fill" : "regular"} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteDoc(d)} data-testid={`delete-doc-${d.id}`} title="Löschen" className="text-rose-600 hover:text-rose-800 hover:bg-rose-50">
                        <Trash size={14} />
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* --- BETRIEBSKOSTEN (2026-02-24) --- */}
        <TabsContent value="betriebskosten" className="space-y-3 pt-4" data-testid="betriebskosten-tab-content">
          <div className="text-sm text-slate-600">
            Betriebskostenabrechnungen (Anschreiben + Abrechnung + Belegverzeichnis).
            Werden automatisch aus dem Modul „Betriebskosten" abgelegt, sobald
            die Berechnung ausgeführt wurde.
          </div>
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <Th>Dokument</Th>
                  <Th>Unterkategorie</Th>
                  <Th>Erstellt</Th>
                  <Th className="text-right">Größe</Th>
                  <Th className="w-24"></Th>
                </tr>
              </thead>
              <tbody>
                {docs.filter((d) => d.category === "Betriebskosten" || d.category === "Nebenkosten").length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-500">
                    Noch keine Betriebskostenabrechnung in diesem Mietverhältnis.
                  </td></tr>
                )}
                {docs
                  .filter((d) => d.category === "Betriebskosten" || d.category === "Nebenkosten")
                  .map((d) => (
                    <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50/70">
                      <Td className="font-medium">{d.title}{d.voided && <span className="ml-1.5 text-[10px] font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-1.5 py-0.5" title={d.void_reason} data-testid={`tenancy-doc-voided-row-badge-${d.id}`}>storniert / gegenstandslos</span>}</Td>
                      <Td className="text-xs text-slate-600">{d.sub_category || "—"}</Td>
                      <Td className="tabular-nums text-xs text-slate-500">{formatDate(docDate(d))}</Td>
                      <Td className="text-right text-xs text-slate-500">{(((d.size || d.size_bytes) || 0) / 1024).toFixed(0)} KB</Td>
                      <Td className="text-right">
                        <DocMetaButton doc={d} onSaved={load} />
                        <Button size="sm" variant="ghost" onClick={() => setPreviewDoc(d)} data-testid={`bk-preview-doc-${d.id}`} title="Vorschau">
                          <Eye size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => downloadDoc(d)} data-testid={`bk-download-doc-${d.id}`} title="Herunterladen">
                          <Download size={14} />
                        </Button>
                      </Td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <PreOwnershipReviewDialog
        open={preOwnershipDialogOpen}
        onOpenChange={setPreOwnershipDialogOpen}
        tenancyId={id}
        onReverted={load}
      />

      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <TenancyDialog
          key={`edit-${id}-${openEdit}`}
          editing={openEdit ? t : null}
          properties={editProperties}
          contacts={editContacts}
          reloadContacts={reloadEditContacts}
          onSaved={() => { setOpenEdit(false); load(); }}
        />
      </Dialog>

      <MessagingDialog
        open={openMsg}
        onOpenChange={(v) => { setOpenMsg(v); if (!v) { setPendingMsgAttachments(null); setPendingMsgSubject(""); setPendingMsgBody(""); setPendingMsgExtra({}); } }}
        context={{ tenancy_id: id, rent_increase_request_id: pendingMsgExtra.rid || undefined, draft_id: pendingMsgExtra.draft || undefined }}
        defaultSubject={pendingMsgSubject || `Mietverhältnis ${t.contract_number || ""}`}
        initialSubSubject={pendingMsgExtra.sub || ""}
        initialMyReference={pendingMsgExtra.myref || ""}
        initialCategory={pendingMsgExtra.category || ""}
        initialFollowUpDate={pendingMsgExtra.follow || ""}
        initialAttachmentDocIds={pendingMsgAttachments}
        initialBody={pendingMsgBody}
      />

      <AppointmentInviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        tenancyId={id}
        defaultKind={t.termination_effective_date || t.return_date || t.termination_date ? "out" : "in"}
        onReady={(d) => {
          setPendingMsgSubject(d.subject || "");
          setPendingMsgBody(d.body_html || "");
          setPendingMsgExtra({ sub: d.sub_subject || "", category: d.category || "" });
          setPendingMsgAttachments(null);
          setOpenMsg(true);
          load();
        }}
      />

      <GenerateContractDialog
        open={openGenerateContract}
        onOpenChange={setOpenGenerateContract}
        tenancyId={id}
        propertyId={t.property_id}
        contactId={(t.tenants && t.tenants[0]?.contact_id) || null}
        onCreated={() => load()}
      />

      <ChangeHistoryDialog
        entityType="tenancy"
        entityId={id}
        title={t.contract_number}
        open={openHistory}
        onOpenChange={setOpenHistory}
      />

      <TerminateDialog
        open={openTerminate}
        onOpenChange={setOpenTerminate}
        tenancy={t}
        onDone={() => { setOpenTerminate(false); load?.(); window.location.reload(); }}
      />

      <TerminationEditDialog
        open={openTerminateEdit}
        onOpenChange={setOpenTerminateEdit}
        tenancy={t}
        onDone={() => { setOpenTerminateEdit(false); load?.(); window.location.reload(); }}
      />

      <DunningTriggerDialog
        open={openDunningTrigger}
        tenancyId={t?.id}
        onClose={() => setOpenDunningTrigger(false)}
        onCreated={() => { setOpenDunningTrigger(false); }}
      />

      {previewDoc && (
        <DocumentPreviewDrawer
          docId={previewDoc.id}
          metaOverride={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}

      <AlertDialog open={openDelete} onOpenChange={setOpenDelete}>
        <AlertDialogContent data-testid="tenancy-delete-confirm">
          <AlertDialogHeader>
            <div className="w-11 h-11 rounded-full bg-rose-50 flex items-center justify-center mb-1">
              <Trash size={20} className="text-rose-600" />
            </div>
            <AlertDialogTitle>Mietverhältnis wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Das Mietverhältnis{" "}
                <span className="font-mono font-medium text-slate-900">{t.contract_number}</span>{" "}
                mit{" "}
                <span className="font-medium text-slate-900">{t.tenant_names?.join(" & ") || "—"}</span>{" "}
                wird in das Archiv verschoben.
              </span>
              <span className="block">
                Die Einheit{" "}
                <span className="font-mono font-medium text-slate-900">{t.unit_label}</span>{" "}
                wird wieder als <span className="font-medium">leerstehend</span> markiert.
                Buchungen und Dokumente bleiben unverändert erhalten.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tenancy-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteTenancy}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="tenancy-delete-confirm-btn"
            >
              <Trash size={14} className="mr-1.5" /> Endgültig archivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 2026-08 — Ratenzahlungsvereinbarung Dialog */}
      <TenancyPaymentPlanDialog
        open={planDialogOpen}
        onClose={() => setPlanDialogOpen(false)}
        tenancyId={id}
        onSaved={load}
      />
    </div>
  );
}


// ---------------- Ratenzahlungspläne im MV-Detail ----------------

function TenancyPaymentPlansPanel({ tenancyId, plans, onReload, onOpenDialog }) {
  const recheck = async (planId) => {
    try {
      await api.post(`/dunning/payment-plans/${planId}/recheck`);
      toast.success("Plan neu geprüft");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };
  const remove = async (planId) => {
    if (!await window._domivusConfirm("Ratenzahlungsvereinbarung wirklich löschen? Ein etwaiger Mahnstopp wird aufgehoben.")) return;
    await api.delete(`/dunning/payment-plans/${planId}`);
    toast.info("Gelöscht");
    onReload();
  };
  const HEALTH = {
    on_track:  { label: "planmäßig", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    overdue:   { label: "überfällig", cls: "bg-rose-100 text-rose-800 border-rose-200" },
    completed: { label: "abgeschlossen", cls: "bg-slate-200 text-slate-700 border-slate-300" },
    unknown:   { label: "unbekannt", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-slate-600">
          Ratenzahlungsvereinbarungen für dieses Mietverhältnis. Solange die Raten planmäßig eingehen,
          pausiert der Zahlungsaufforderungs-Lauf automatisch.
        </div>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1.5" onClick={onOpenDialog} data-testid="tenancy-plan-new">
          <Plus size={13} /> Neue Vereinbarung
        </Button>
      </div>
      {plans.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-md p-8 text-center text-slate-500 text-sm">
          <HandCoins size={28} className="mx-auto mb-2 text-slate-300" />
          Keine Ratenzahlungsvereinbarung vorhanden.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Start</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600 text-right">Rate/Monat</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600 text-right">Fortschritt</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600 text-right">Bezahlt</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Status</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Grund</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => {
                const h = p.health || {};
                const badge = HEALTH[h.status || "unknown"];
                return (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`tenancy-plan-${p.id}`}>
                    <td className="px-3 py-2 text-xs">{formatDate(p.start_date)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatEUR(p.monthly_amount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{h.installments_paid ?? 0} / {p.installments}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{formatEUR(h.paid_amount || 0)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{p.reason || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => recheck(p.id)} title="Status neu berechnen" data-testid={`tenancy-plan-recheck-${p.id}`}>
                        <ArrowsClockwise size={13} />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => remove(p.id)} data-testid={`tenancy-plan-delete-${p.id}`}>
                        <Trash size={13} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TenancyPaymentPlanDialog({ open, onClose, tenancyId, onSaved }) {
  const [form, setForm] = useState({
    start_date: new Date().toISOString().slice(0, 10),
    monthly_amount: 100, installments: 6, reason: "", notes: "",
  });
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      await api.post("/dunning/payment-plans", {
        tenancy_id: tenancyId,
        start_date: form.start_date,
        monthly_amount: Number(form.monthly_amount),
        installments: Number(form.installments),
        reason: form.reason || null,
        notes: form.notes || null,
      });
      toast.success("Ratenzahlung angelegt");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="tenancy-plan-dialog">
        <DialogHeader><DialogTitle>Neue Ratenzahlungsvereinbarung</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="text-xs font-medium text-slate-700 mb-1">Startdatum *</div>
              <Input type="date" value={form.start_date} onChange={(e) => setField("start_date", e.target.value)} data-testid="t-plan-start" />
            </div>
            <div>
              <div className="text-xs font-medium text-slate-700 mb-1">Rate/Monat *</div>
              <Input type="number" step="0.01" min="1" value={form.monthly_amount}
                     onChange={(e) => setField("monthly_amount", e.target.value)} data-testid="t-plan-amount" />
            </div>
            <div>
              <div className="text-xs font-medium text-slate-700 mb-1">Anzahl Raten *</div>
              <Input type="number" min="1" max="60" value={form.installments}
                     onChange={(e) => setField("installments", e.target.value)} data-testid="t-plan-installments" />
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-700 mb-1">Grund</div>
            <Input value={form.reason} onChange={(e) => setField("reason", e.target.value)} placeholder="z. B. Nebenkosten-Nachzahlung" />
          </div>
          <div>
            <div className="text-xs font-medium text-slate-700 mb-1">Notizen</div>
            <Input value={form.notes} onChange={(e) => setField("notes", e.target.value)} />
          </div>
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
            Eingehende Zahlungen werden automatisch gegen die Raten geprüft. Solange planmäßig gezahlt wird, pausiert der Zahlungsaufforderungs-Lauf.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="t-plan-save">Anlegen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* iter144 — Rückstands-Detail Card: welche Monate sind offen? */
function ArrearsBreakdownCard({ tenancyId, onChanged, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [markingPeriod, setMarkingPeriod] = useState(null);
  // 2026-10 · Detail-Modus: pro Periode einklappbare Zeilen (Forderungsart-Detail)
  const [expandedPeriods, setExpandedPeriods] = useState({});
  const confirm = useConfirm();
  const reload = () => {
    setLoading(true);
    api.get(`/dunning/analyze/${tenancyId}`)
      .then((r) => { setData(r.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/dunning/analyze/${tenancyId}`)
      .then((r) => { if (alive) setData(r.data); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tenancyId, refreshKey]);

  const markPaid = async (period, totalOpen) => {
    // iter159p — Domivus-Dialog statt window.confirm
    const ok = await confirm({
      title: "Fälligkeit ausgleichen?",
      description: `Möchten Sie die Fälligkeit ${period} (${fmtEUR(totalOpen)}) als komplett ausgeglichen buchen? Es werden Ist-Buchungen zum jeweiligen Fälligkeitsdatum ergänzt.`,
      confirmLabel: "Ja, ausgleichen",
      cancelLabel: "Abbrechen",
    });
    if (!ok) return;
    setMarkingPeriod(period);
    try {
      const { data: res } = await api.post(`/tenancies/${tenancyId}/mark-period-paid`, { period });
      toast.success(`Fälligkeit ${period} ausgeglichen: ${res.postings?.length || 0} Buchung(en), Gesamt ${fmtEUR(res.total)}`);
      reload();
      // iter159p — Auch das obere Mietkonto (Soll/Ist/Saldo) neu laden.
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Ausgleichen");
    } finally {
      setMarkingPeriod(null);
    }
  };

  const fmtEUR = (v) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v || 0));

  if (loading) return null;
  const rent = data?.rent;
  const total = Number(rent?.outstanding_total_eur || 0);
  if (!rent || total <= 0) {
    return (
      <div className="border border-emerald-200 bg-emerald-50 rounded-xl px-5 py-2.5 text-sm flex items-center gap-2" data-testid="arrears-card-clean">
        <CheckIcon size={16} className="text-emerald-700" />
        <span className="text-emerald-900 font-medium">Keine offenen Mietforderungen — Konto ist ausgeglichen.</span>
      </div>
    );
  }
  const openList = rent.open_by_period || [];
  // 2026-10 · Reiches Ledger-Detail (falls Endpoint es liefert) für die
  // ausklappbare Detailansicht pro Periode (Forderungsart × Werte).
  const ledgerPeriods = data?.ledger?.periods || [];
  const ledgerByPeriod = Object.fromEntries(ledgerPeriods.map((p) => [p.period, p]));
  // iter165 · Bug 8: "Bereits getilgt durch" nur zeigen, wenn das Konto
  // tatsächlich einen Überschuss hat ODER die Tilgung aus einer anderen
  // Periode stammt (FIFO-Waterfall aus Überzahlung/Rate). So werden
  // explizit zugewiesene Monatszahlungen NICHT als „auf alte Rückstände
  // angewendet" dargestellt.
  const hasSurplus = Number(data?.ledger?.saldo_eur ?? data?.ledger?.balance ?? 0) > 0.005;
  const CAT_LABEL = {
    rent_cold: "Kaltmiete", parking: "Stellplatz", vat: "USt",
    utilities: "Nebenkosten", heating: "Heizkosten",
    bka: "BKA-Nachzahlung", other: "Sonstiges",
    fee: "Gebühr", dunning_fee: "Mahngebühr",
    surplus: "Überschuss", claim: "Forderung", damages: "Schadensersatz",
    interest: "Verzugszinsen", legal_costs: "Rechtsverfolgungskosten",
  };
  const togglePeriod = (period) =>
    setExpandedPeriods((prev) => ({ ...prev, [period]: !prev[period] }));
  const fmtPeriod = (p) => {
    if (!p) return "—";
    const [y, m] = p.split("-");
    if (!y || !m) return p;
    const monthNames = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];
    return `${monthNames[Number(m)-1] || m} ${y}`;
  };
  // iter159d — Ab 5 offene Perioden: zusammenklappen (nur die 3 neuesten sichtbar).
  const COLLAPSE_THRESHOLD = 5;
  const shouldCollapse = openList.length > COLLAPSE_THRESHOLD && !expanded;
  const visibleList = shouldCollapse ? openList.slice(-3) : openList;
  const hiddenCount = openList.length - visibleList.length;
  return (
    <div className="border border-amber-300 bg-amber-50 rounded-xl px-5 py-3 shadow-sm" data-testid="arrears-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-amber-900">Offene Forderungen: {fmtEUR(total)}</div>
          <div className="text-xs text-amber-800 mt-0.5">
            {rent.scenario_label} · Erster Rückstand: {rent.erster_rueckstand ? new Date(rent.erster_rueckstand).toLocaleDateString("de-DE") : "—"} · Letzte Fälligkeit: {rent.letzte_faelligkeit ? new Date(rent.letzte_faelligkeit).toLocaleDateString("de-DE") : "—"}
          </div>
        </div>
      </div>
      {openList.length > 0 && (
        <div className="mt-2 border-t border-amber-200 pt-2">
          {shouldCollapse && (
            <div className="flex items-center justify-between mb-1">
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="text-xs font-medium text-amber-900 hover:text-amber-700 underline underline-offset-2"
                data-testid="arrears-expand-btn"
              >
                {hiddenCount} weitere Fälligkeit{hiddenCount === 1 ? "" : "en"} anzeigen ▾
              </button>
            </div>
          )}
          {expanded && openList.length > COLLAPSE_THRESHOLD && (
            <div className="flex items-center justify-between mb-1">
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="text-xs font-medium text-amber-900 hover:text-amber-700 underline underline-offset-2"
                data-testid="arrears-collapse-btn"
              >
                Nur letzte 3 anzeigen ▴
              </button>
            </div>
          )}
          <div className="md:hidden space-y-2" data-testid="arrears-mobile-list">
            {visibleList.map((r) => (
              <div key={r.period} className={`rounded border border-amber-200 bg-white/70 p-2.5 ${r.is_current ? "ring-1 ring-amber-300" : ""}`}
                   data-testid={`arrears-card-${r.period}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-amber-950">
                    {fmtPeriod(r.period)}
                    {r.is_current && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-200 text-amber-900 font-semibold">aktuell</span>}
                    {r.is_overdue && !r.is_current && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-rose-200 text-rose-900 font-semibold">überfällig</span>}
                  </div>
                  <div className="text-sm font-bold tabular-nums text-amber-950">{fmtEUR(r.total_open)}</div>
                </div>
                <div className="text-[11px] text-amber-800 mt-0.5">
                  {[r.rent_open > 0 && `Kalt ${fmtEUR(r.rent_open)}`, r.nk_open > 0 && `NK ${fmtEUR(r.nk_open)}`, r.bka_open > 0 && `BKA ${fmtEUR(r.bka_open)}`].filter(Boolean).join(" · ") || "—"}
                  {r.due_date && <span className="ml-1 text-amber-700">· fällig {new Date(r.due_date).toLocaleDateString("de-DE")}</span>}
                </div>
                <button type="button" onClick={() => markPaid(r.period, r.total_open)} disabled={markingPeriod === r.period}
                        className="mt-2 w-full text-[12px] py-1.5 rounded border border-emerald-400 text-emerald-800 bg-white hover:bg-emerald-50 disabled:opacity-50"
                        data-testid={`arrears-card-mark-paid-${r.period}`}>
                  {markingPeriod === r.period ? "…" : "Ausgleichen"}
                </button>
              </div>
            ))}
          </div>
          <table className="min-w-full text-xs hidden md:table" data-testid="arrears-table">
            <thead>
              <tr className="text-left text-amber-900">
                <th className="py-1">Monat</th>
                <th className="py-1 text-right">Kaltmiete offen</th>
                <th className="py-1 text-right">Nebenkosten offen</th>
                <th className="py-1 text-right">BKA offen</th>
                <th className="py-1 text-right font-semibold">Gesamt</th>
                <th className="py-1 text-right w-32"></th>
              </tr>
            </thead>
            <tbody>
              {visibleList.map((r) => {
                const appliedRaw = r.applied_from || [];
                // iter165 · Bug 8: Nur Tilgungen anzeigen, deren Ursprung
                // in einem ANDEREN Monat als der aktuellen Rückstands-Periode
                // liegt (echte FIFO-Verwendung aus Überschuss/Rate). Wenn
                // Zahlung/Rate = Periode → das ist die reguläre eigene
                // Zahlung, keine Umschichtung — kein „bereits getilgt durch".
                // Zusätzlich: Auto-Bank-Überzahlungen (kind=bank ohne plan_id)
                // erscheinen nur, wenn das Konto insgesamt einen Überschuss hat.
                const applied = appliedRaw.filter((src) => {
                  const isRate = !!src.plan_id;
                  const fromDifferentPeriod = src.posting_date
                    && String(src.posting_date).slice(0, 7) !== r.period;
                  if (isRate) return true; // Ratenzahlungen sind explizit Alt-Tilgung
                  if (fromDifferentPeriod) return hasSurplus; // Nur bei tatsächlichem Überschuss
                  return false;
                });
                return (
                <React.Fragment key={r.period}>
                <tr className={`border-t border-amber-100 ${r.is_current ? "bg-amber-100/60" : ""}`}>
                  <td className="py-1">
                    <button type="button"
                            onClick={() => togglePeriod(r.period)}
                            className="mr-1 text-[10px] text-amber-800 hover:text-amber-950 font-mono"
                            data-testid={`arrears-period-toggle-${r.period}`}
                            title="Detail zur Periode ein-/ausklappen">
                      {expandedPeriods[r.period] ? "▾" : "▸"}
                    </button>
                    {fmtPeriod(r.period)}
                    {r.is_current && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-200 text-amber-900 font-semibold">aktuell</span>}
                    {r.is_overdue && !r.is_current && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-rose-200 text-rose-900 font-semibold" data-testid={`arrears-overdue-${r.period}`}>überfällig</span>}
                    {r.due_date && <span className="ml-1 text-[9px] text-amber-700">fällig {new Date(r.due_date).toLocaleDateString("de-DE")}</span>}
                  </td>
                  <td className="py-1 text-right tabular-nums">{r.rent_open > 0 ? fmtEUR(r.rent_open) : "—"}</td>
                  <td className="py-1 text-right tabular-nums">{r.nk_open > 0 ? fmtEUR(r.nk_open) : "—"}</td>
                  <td className="py-1 text-right tabular-nums">{r.bka_open > 0 ? fmtEUR(r.bka_open) : "—"}</td>
                  <td className="py-1 text-right tabular-nums font-semibold">{fmtEUR(r.total_open)}</td>
                  <td className="py-1 text-right">
                    <button
                      type="button"
                      onClick={() => markPaid(r.period, r.total_open)}
                      disabled={markingPeriod === r.period}
                      className="text-[11px] px-2 py-0.5 rounded border border-emerald-400 text-emerald-800 bg-white hover:bg-emerald-50 disabled:opacity-50"
                      data-testid={`arrears-mark-paid-${r.period}`}
                      title="Diese Fälligkeit als komplett ausgeglichen buchen"
                    >
                      {markingPeriod === r.period ? "…" : "Ausgleichen"}
                    </button>
                  </td>
                </tr>
                {expandedPeriods[r.period] && ledgerByPeriod[r.period] && (
                  <tr className="border-t-0 bg-white/60">
                    <td colSpan={6} className="py-1.5 pl-6 pr-2">
                      <table className="w-full text-[11px]" data-testid={`arrears-detail-${r.period}`}>
                        <thead>
                          <tr className="text-amber-900 border-b border-amber-200">
                            <th className="py-0.5 text-left font-medium">Forderungsart</th>
                            <th className="py-0.5 text-right font-medium">Sollbetrag</th>
                            <th className="py-0.5 text-right font-medium">Ausgeglichen</th>
                            <th className="py-0.5 text-right font-medium">Noch offen</th>
                            <th className="py-0.5 text-left font-medium pl-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ledgerByPeriod[r.period].lines.map((ln, li) => {
                            const status = ln.is_historic ? "historisch"
                              : ln.open <= 0.005 ? "ausgeglichen"
                              : ln.settled > 0.005 ? "teilweise"
                              : (r.is_overdue ? "überfällig" : "offen");
                            const statusColor = {
                              historisch: "text-slate-500 bg-slate-100 border-slate-200",
                              ausgeglichen: "text-emerald-800 bg-emerald-100 border-emerald-200",
                              teilweise: "text-amber-800 bg-amber-100 border-amber-300",
                              überfällig: "text-rose-800 bg-rose-100 border-rose-300",
                              offen: "text-slate-700 bg-white border-slate-300",
                            }[status];
                            return (
                              <tr key={li} className="border-b border-amber-100/60">
                                <td className="py-0.5">{CAT_LABEL[ln.category] || ln.category}</td>
                                <td className="py-0.5 text-right tabular-nums">{fmtEUR(ln.soll)}</td>
                                <td className="py-0.5 text-right tabular-nums text-emerald-700">{fmtEUR(ln.settled)}</td>
                                <td className="py-0.5 text-right tabular-nums font-semibold">{fmtEUR(ln.open)}</td>
                                <td className="py-0.5 pl-3">
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${statusColor}`}>
                                    {status}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
                {applied.length > 0 && (
                  <tr className={`border-t-0 ${r.is_current ? "bg-amber-100/40" : "bg-emerald-50/60"}`}>
                    <td colSpan={6} className="py-1 pl-3">
                      <div className="flex flex-wrap gap-1.5 items-center">
                        <span className="text-[10px] text-emerald-800 font-medium">
                          Bereits getilgt durch:
                        </span>
                        {applied.map((src, ix) => {
                          const isRate = !!src.plan_id;
                          const catLabel = src.category === "rent_cold" ? "Kaltmiete"
                            : src.category === "utilities" || src.category === "heating" ? "Nebenkosten"
                            : src.category === "bka" || src.category === "other" ? "BKA"
                            : src.category;
                          return (
                            <span key={ix}
                                  data-testid={`arrears-applied-${r.period}-${ix}`}
                                  className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                    isRate
                                      ? "bg-sky-100 text-sky-900 border-sky-300"
                                      : "bg-emerald-100 text-emerald-900 border-emerald-200"
                                  }`}>
                              {src.source} — {fmtEUR(src.amount)} ({catLabel})
                            </span>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

